import React, { useEffect, useLayoutEffect, useRef, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { App, Breadcrumb, Button, Drawer, Input, Modal, Space, Spin, Tooltip } from 'antd';
import { FlowMenu } from '@/components/flow-dropdown';
import type { FlowDropdownGroup, FlowDropdownItem } from '@/components/flow-dropdown';
import G6, { Minimap, TreeGraph } from '@antv/g6';
import type { IG6GraphEvent, Item } from '@antv/g6';
import {
  Tree2Outline,
  ProjectOutline,
  CircleCheckFill,
  Charts2Outline,
  ChartsSetOutline,
  CircleHalfCheckOutline,
  CircleXFill,
  CircleTrendingDown2Fill,
  ShareBoxOutline,
  ArrowDownDoubleOutline,
  ArrowUpDoubleOutline,
  FileDescriptionOutline,
  GitForkOutline,
  InsertOutline,
  CopyOutline,
  CloseOutline,
  CompareOutline,
  TrashOutline,
  PencilOutline,
  CaseOutline,
  VolumeOutline,
} from '@flexcompute/icons-antd';
import type { ProjectDetail } from '@/apis/types/project';
import type { ProjectItem, WorkspaceItemType } from '@/apis/endpoints/project';
import { projectApi } from '@/apis/endpoints/project';
import { companyWorkspaceApi } from '@/apis/endpoints/company-workspace';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateProjectModal } from '@/components/create-project-modal';
import { PRIVATE_WORKSPACE_ROOT_FOLDER_NAME, ROOT_FOLDER_ID } from '@/components/folder-tree/constants';
import { folderApi } from '@/apis/endpoints';
import { workspaceContextStore } from '@/stores';
import { useAuthStore } from '@/hooks/use-auth-hook';
import { buildTree } from '@/utils/tree';
import { useUserHook } from '@/hooks/use-user-hook';
import { usePreferenceStore } from '@/stores/preference-store';
import { captureModifierState, navigateToProject } from '@/utils/navigation';
import { useWebSocketHook } from '@/hooks/use-websocket';
import { useResourceProgressStore } from '@/pages/workbench/stores/resource-progress';
import { logBehavior } from '@/utils/logger';
import { Color } from './constants';
import { CHECKBOX_SHAPE, INTER_BOX_SHAPE, INTER_ICON_SHAPE, type SetNodeStateType } from './register-project-node';
import './register-project-node';
import styles from './index.module.less';
import { DraftList } from './components/draft-list';
import { InterItemSelectModal } from './components/inter-item-select-modal';
import { ProjectTreeDetailsDrawer } from './components/details-drawer';
import { copyText } from './components/details-drawer/utils';
import ProjectTreeShortcutButton from './components/shortcut-button';
import { BatchDeleteAssetModal } from '@/components/batch-delete-asset-modal';
import { EditItemModal } from '@/pages/workspace/components/edit-item-modal';
import type { WorkspaceItemType as WorkspaceItemTypeFromWorkspace, WorkspaceItem } from '@/apis/endpoints/workspace';
import type { BatchDeleteAsset } from '@/components/batch-delete-asset-modal';

const MOCK_ROOT_NODE_ID = 'MOCK_ROOT_NODE_ID';

const CONTEXT_MENU_VIEWPORT_MARGIN = 8;
const DEFAULT_MENU_POSITION: MenuPosition = { x: 0, y: 0 };

type MenuPosition = {
  x: number;
  y: number;
};

function clampMenuPositionToViewport(position: MenuPosition, menuElement: HTMLDivElement | null): MenuPosition {
  if (!menuElement || typeof window === 'undefined') {
    return position;
  }

  // Keep portaled menus fully reachable when users open them near viewport edges.
  const { width, height } = menuElement.getBoundingClientRect();
  const maxX = Math.max(CONTEXT_MENU_VIEWPORT_MARGIN, window.innerWidth - width - CONTEXT_MENU_VIEWPORT_MARGIN);
  const maxY = Math.max(CONTEXT_MENU_VIEWPORT_MARGIN, window.innerHeight - height - CONTEXT_MENU_VIEWPORT_MARGIN);

  return {
    x: Math.min(Math.max(position.x, CONTEXT_MENU_VIEWPORT_MARGIN), maxX),
    y: Math.min(Math.max(position.y, CONTEXT_MENU_VIEWPORT_MARGIN), maxY),
  };
}

export interface ProjectTreeViewEventData {
  item: ProjectItem;
  event?: React.MouseEvent;
  modifierPressed?: boolean;
}

export interface ProjectTreeNode extends ProjectItem {
  itemType?: ProjectItem['type']; // Alias for type, used in G6 node rendering
  parentNodeId?: string | null;
  parentCaseId?: string;
  children?: ProjectTreeNode[];
  collapsed?: boolean;
  highlight?: boolean;
  checked?: boolean;
  hovered?: boolean;
  progress?: number;
  isCurrent?: boolean;
  // Interpolation-related fields
  isInter?: boolean;
  isVolumeInter?: boolean;
  isCaseInter?: boolean;
  interHovered?: boolean;
  isInterItemOuterProject?: boolean;
  // whether the inter reference (source item) is from a different project than the current viewing project
  isInterRefOuterProject?: boolean;
  interItemDeleted?: boolean;
  interItemType?: 'Case' | 'VolumeMesh';
  interItemId?: string;
  interItemName?: string;
  interItemProjectId?: string;
  interItemProjectName?: string;
  // Additional status fields
  postProcessStatus?: ProjectItem['postProcessStatus'];
  createdAt?: string;
  solverUpdatedAt?: string;
  // Project ID for interpolation checks
  projectId?: string;
  // Parent ID for interpolation checks
  parentId?: string;
}

type GraphItemState = SetNodeStateType | 'default';

type DraftCreatedContext = {
  action: 'copy' | 'fork' | 'interpolation';
  node: ProjectTreeNode;
  openInNewTab?: boolean;
  modifierPressed?: boolean;
};

interface ModifierKeyEvent {
  ctrlKey?: boolean;
  metaKey?: boolean;
}

type DisplayStatus = 'submitted' | 'running' | 'completed' | 'error' | 'diverged';

function isDisplayStatusFinished(status: DisplayStatus | undefined | null): boolean {
  return status === 'completed' || status === 'error' || status === 'diverged';
}

type SupportedItemTypeForDisplayStatus = 'Geometry' | 'SurfaceMesh' | 'VolumeMesh' | 'Case';
type DisplayStatusMap = Record<string, DisplayStatus>;
type TypeStatusMap = Record<SupportedItemTypeForDisplayStatus, DisplayStatusMap>;

const GEOMETRY_STATUS_MAP: DisplayStatusMap = {
  uploading: 'submitted',
  uploaded: 'submitted',
  generating: 'running',
  processed: 'completed',
  error: 'error',
};
const SURFACE_STATUS_MAP: DisplayStatusMap = {
  pending: 'submitted',
  generating: 'running',
  processed: 'completed',
  error: 'error',
};
const VOLUME_STATUS_MAP: DisplayStatusMap = {
  uploading: 'submitted',
  pending: 'submitted',
  generating: 'running',
  uploaded: 'completed',
  completed: 'completed',
  error: 'error',
};
const CASE_STATUS_MAP: DisplayStatusMap = {
  pending: 'submitted',
  uploaded: 'submitted',
  preprocessing: 'running',
  running: 'running',
  completed: 'completed',
  error: 'error',
  diverged: 'diverged',
};

const TYPE_STATUS_MAP: TypeStatusMap = {
  Geometry: GEOMETRY_STATUS_MAP,
  SurfaceMesh: SURFACE_STATUS_MAP,
  VolumeMesh: VOLUME_STATUS_MAP,
  Case: CASE_STATUS_MAP,
};

function getDisplayStatusFromLegacyStatus(item: { type?: string; status?: string }): DisplayStatus | undefined {
  const { type, status } = item;
  if (!type || !status) return undefined;
  return TYPE_STATUS_MAP[type as SupportedItemTypeForDisplayStatus]?.[status] ?? undefined;
}

function getProjectTreeProgress(item: { id?: string; currentProgress?: ProjectItem['currentProgress'] }): number {
  if (!item.id) {
    return item.currentProgress?.percentage ?? 0;
  }
  const cachedProgress = useResourceProgressStore.getState().getProgressById(item.id);
  return cachedProgress || item.currentProgress?.percentage || 0;
}

interface ProjectTreeProps {
  visible: boolean;
  onVisibleChange: (visible: boolean) => void;
  currentProject: ProjectDetail;
  viewMode?: boolean;
  // When provided, the tree syncs draft/current-item state with the parent workbench.
  currentItem?: ProjectItem | null;
  onCurrentItemChange?: (item: ProjectItem | null) => void;
  onViewEvent?: (data: ProjectTreeViewEventData) => void;
  onNewDraftEvent?: (node: ProjectTreeNode) => void;
  onDeleteEvent?: (item: ProjectItem) => void;
  /**
   * Callback after a draft is created successfully (copy/fork/interpolation).
   * Default behavior (if not provided): update draft list only (optimistic cache + invalidate).
   */
  onDraftCreated?: (draft: ProjectItem, context: DraftCreatedContext) => void;
  isCurrentDraftLocked?: boolean;
}

export const ProjectTree: React.FC<ProjectTreeProps> = ({
  visible,
  onVisibleChange,
  currentProject,
  viewMode = false,
  currentItem,
  onCurrentItemChange,
  onViewEvent,
  onNewDraftEvent,
  onDraftCreated,
  isCurrentDraftLocked = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onDeleteEvent: _onDeleteEvent,
}) => {
  const { modal: modalApi, message: messageApi } = App.useApp();
  const { onMessage, watch: watchWebSocket } = useWebSocketHook();
  const isMacPlatform = typeof navigator !== 'undefined' && /mac|iphone|ipad|ipod/i.test(navigator.platform);
  const [isPrimaryModifierPressed, setIsPrimaryModifierPressed] = useState(false);
  const syncsWithCurrentItem = currentItem !== undefined;

  // Keep a local fallback for uncontrolled usage when currentItem isn't provided.
  const [internalCurrentItem, setInternalCurrentItem] = useState<ProjectItem | null>(currentItem || null);

  // Sync external currentItem changes
  useEffect(() => {
    if (currentItem !== undefined) {
      setInternalCurrentItem(currentItem);
    }
  }, [currentItem]);

  // Notify parent of currentItem changes when the tree is used in controlled mode.
  const handleCurrentItemChange = useCallback(
    (item: ProjectItem | null) => {
      setInternalCurrentItem(item);
      if (onCurrentItemChange) {
        onCurrentItemChange(item);
      }
    },
    [onCurrentItemChange],
  );

  // Support both controlled (prop-driven) and uncontrolled usage.
  const effectiveCurrentItem = currentItem !== undefined ? currentItem : internalCurrentItem;

  // Expose handleCurrentItemChange for use in event handlers
  const updateCurrentItem = useCallback(
    (item: ProjectItem | null) => {
      handleCurrentItemChange(item);
    },
    [handleCurrentItemChange],
  );

  const isPrimaryModifierEvent = useCallback(
    (event?: ModifierKeyEvent | null): boolean => Boolean(isMacPlatform ? event?.metaKey : event?.ctrlKey),
    [isMacPlatform],
  );

  useEffect(() => {
    // Match legacy project-tree menus: holding the primary modifier switches actions into "open in new tab" mode.
    const handleKeyEvent = (event: KeyboardEvent) => {
      setIsPrimaryModifierPressed(isPrimaryModifierEvent(event));
    };

    const handleBlur = () => {
      window.setTimeout(() => setIsPrimaryModifierPressed(false));
    };

    window.addEventListener('keydown', handleKeyEvent, true);
    window.addEventListener('keyup', handleKeyEvent, true);
    window.addEventListener('blur', handleBlur, true);

    return () => {
      window.removeEventListener('keydown', handleKeyEvent, true);
      window.removeEventListener('keyup', handleKeyEvent, true);
      window.removeEventListener('blur', handleBlur, true);
    };
  }, [isPrimaryModifierEvent]);
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<TreeGraph | null>(null);
  const treeItemMapRef = useRef<Map<string, ProjectTreeNode>>(new Map());
  const currentItemAssetIdRef = useRef<string>('');
  const currentTreeNodeIdRef = useRef<string>('');
  const highlightPathIdsRef = useRef<string[]>([]);
  const setupInterItemFieldsRef = useRef<(node: ProjectTreeNode) => void>(() => undefined);
  const draftRecordsRef = useRef<ProjectItem[]>([]);
  const locateNodeRef = useRef<(nodeId: string) => void>(() => undefined);
  const viewModeRef = useRef<boolean>(viewMode);

  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  const getRootWorkbenchItem = useCallback((): ProjectItem => {
    const map = treeItemMapRef.current;
    const rootNode = map.get(currentProject.rootItemId);
    const rootType = (rootNode?.itemType || rootNode?.type || currentProject.rootItemType) as WorkspaceItemType;
    return {
      id: currentProject.rootItemId,
      type: rootType,
      name: rootNode?.name ?? currentProject.name,
    } as ProjectItem;
  }, [currentProject.name, currentProject.rootItemId, currentProject.rootItemType]);

  const switchToClosestParentOrRoot = useCallback(
    (deletedIds: Set<string>) => {
      const map = treeItemMapRef.current;
      const viewingId = currentItemAssetIdRef.current;
      if (!viewingId) {
        const root = getRootWorkbenchItem();
        if (onViewEvent) onViewEvent({ item: root, event: undefined });
        else updateCurrentItem(root);
        return;
      }

      // Align with Angular behavior: if current viewing item is deleted, find the closest parent that is not deleted.
      let curId: string | undefined = viewingId;
      while (curId && deletedIds.has(curId)) {
        const node = map.get(curId);
        const parentId = node?.parentNodeId ?? '';
        curId = parentId || undefined;
      }

      const nextNode = curId ? map.get(curId) : undefined;
      const nextItem: ProjectItem =
        nextNode && !deletedIds.has(nextNode.id)
          ? ({
              ...nextNode,
              type: (nextNode.itemType || nextNode.type) as WorkspaceItemType,
            } as ProjectItem)
          : getRootWorkbenchItem();

      if (onViewEvent) onViewEvent({ item: nextItem, event: undefined });
      else updateCurrentItem(nextItem);
    },
    [getRootWorkbenchItem, onViewEvent, updateCurrentItem],
  );

  const switchToAnotherDraftOrRoot = useCallback(
    (opts?: { excludeDraftIds?: Set<string> }) => {
      const excludeDraftIds = opts?.excludeDraftIds ?? new Set<string>();
      const nextDraft = !viewModeRef.current
        ? draftRecordsRef.current.find((d) => d.id && !excludeDraftIds.has(d.id))
        : undefined;
      const nextItem = (nextDraft ?? getRootWorkbenchItem()) as ProjectItem;
      if (onViewEvent) {
        onViewEvent({ item: nextItem, event: undefined });
      } else {
        updateCurrentItem(nextItem);
      }
    },
    [getRootWorkbenchItem, onViewEvent, updateCurrentItem],
  );

  // Keep node data update consistent with legacy behavior:
  // - update node model via graph.updateItem
  // - optionally trigger partial redraw via graph.setItemState
  const updateGraphItem = useCallback(
    (itemOrId: Item | string, data: Partial<ProjectTreeNode>, state?: GraphItemState) => {
      const graph = graphRef.current;
      if (!graph) return;
      const item = typeof itemOrId === 'string' ? graph.findById(itemOrId) : itemOrId;
      if (!item) return;
      // G6 internal setOriginStyle accesses group.get("children").length which throws
      // if the item's shape group is not fully initialized (e.g. graph still rendering).
      const group = item.getContainer?.();
      if (!group?.get('children')) return;
      graph.updateItem(item, data);
      if (state) {
        const nodeModel = item.getModel() as unknown as ProjectTreeNode;
        const stateValue =
          state === 'default' ? true : Boolean((nodeModel as unknown as Record<string, unknown>)[state]);
        graph.setItemState(item, state, stateValue);
      }
    },
    [],
  );

  // Keep selection/highlight styling outside treeData to avoid relayout on parent state refreshes.
  const syncGraphNodeVisualState = useCallback((graph: TreeGraph, highlightIds: string[], currentNodeId: string) => {
    const highlightSet = new Set(highlightIds);

    graph.getNodes().forEach((node) => {
      const group = node.getContainer?.();
      if (!group?.get('children')) return;
      const model = node.getModel() as unknown as ProjectTreeNode;
      const shouldHighlight = highlightSet.has(model.id);
      const shouldBeCurrent = model.id === currentNodeId;

      if (model.highlight !== shouldHighlight || model.isCurrent !== shouldBeCurrent) {
        graph.updateItem(node, {
          highlight: shouldHighlight,
          isCurrent: shouldBeCurrent,
        });
        graph.setItemState(node, 'default', true);
      } else {
        graph.setItemState(node, 'highlight', shouldHighlight);
      }
    });

    graph.getEdges().forEach((edge) => {
      const group = edge.getContainer?.();
      if (!group?.get('children')) return;
      const sourceId = edge.getSource().getID();
      const targetId = edge.getTarget().getID();
      const shouldHighlight = highlightSet.has(sourceId) && highlightSet.has(targetId);
      graph.updateItem(edge, {
        style: {
          stroke: shouldHighlight ? Color.highlight : Color.edge,
        },
      });
      if (shouldHighlight) {
        edge.toFront();
      } else {
        edge.toBack();
      }
    });
  }, []);

  // Context menu state
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState<MenuPosition>(DEFAULT_MENU_POSITION);
  const [contextMenuNode, setContextMenuNode] = useState<ProjectTreeNode | null>(null);
  const contextMenuContainerRef = useRef<HTMLDivElement | null>(null);

  // Interpolation context menu state
  const [interContextMenuVisible, setInterContextMenuVisible] = useState(false);
  const [interContextMenuPosition, setInterContextMenuPosition] = useState<MenuPosition>(DEFAULT_MENU_POSITION);
  const [interContextMenuNode, setInterContextMenuNode] = useState<ProjectTreeNode | null>(null);
  const interContextMenuContainerRef = useRef<HTMLDivElement | null>(null);

  // Interpolation hover tooltip state
  const [interTooltipVisible, setInterTooltipVisible] = useState(false);
  const [interTooltipNode, setInterTooltipNode] = useState<ProjectTreeNode | null>(null);
  const [interTooltipStyle, setInterTooltipStyle] = useState<React.CSSProperties>({});

  // Details drawer state
  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [detailsDrawerNode, setDetailsDrawerNode] = useState<ProjectTreeNode | null>(null);

  // New project modal state
  const [newProjectModalVisible, setNewProjectModalVisible] = useState(false);
  const [newProjectCloneNode, setNewProjectCloneNode] = useState<ProjectTreeNode | null>(null);

  // Interpolation select modal state
  const [interItemSelectModalVisible, setInterItemSelectModalVisible] = useState(false);
  const [interItemSelectModalLoading, setInterItemSelectModalLoading] = useState(false);
  const [interOperationNode, setInterOperationNode] = useState<ProjectTreeNode | null>(null);

  const queryClient = useQueryClient();
  const { guestUser } = useAuthStore();
  const { getAuthPath } = useUserHook();
  const scopedWorkspaceId = workspaceContextStore((state) => state.workspaceId);

  const currentProjectFolderId = currentProject.parentFolderId || ROOT_FOLDER_ID;
  const openWorkbenchItemInNewTab = useCallback(
    (projectId: string, itemId: string, itemType: string, modifierPressed?: boolean) => {
      const url = new URL(`/workbench/${projectId}`, window.location.origin);
      url.searchParams.set('id', itemId);
      url.searchParams.set('type', itemType);
      navigateToProject(`${url.pathname}${url.search}`, getAuthPath, modifierPressed);
    },
    [getAuthPath],
  );

  const folderMetadataQuery = useQuery({
    queryKey: ['folderMetadata', guestUser.userId, currentProjectFolderId],
    queryFn: () => folderApi.getFolderMetadata(currentProjectFolderId),
    enabled: visible && !viewMode && currentProjectFolderId !== ROOT_FOLDER_ID,
  });

  const effectiveWorkspaceId = useMemo(() => {
    return (currentProject.workspaceId ?? folderMetadataQuery.data?.workspaceId ?? scopedWorkspaceId ?? '').trim();
  }, [currentProject.workspaceId, folderMetadataQuery.data?.workspaceId, scopedWorkspaceId]);

  const workspaceListQuery = useQuery({
    queryKey: ['company-workspaces', guestUser.userId, effectiveWorkspaceId],
    queryFn: () => companyWorkspaceApi.getWorkspaceList(),
    enabled: visible && !viewMode && Boolean(guestUser.userId && effectiveWorkspaceId),
  });

  const effectiveWorkspace = useMemo(() => {
    return workspaceListQuery.data?.find((workspace) => workspace.id === effectiveWorkspaceId) ?? null;
  }, [effectiveWorkspaceId, workspaceListQuery.data]);

  useEffect(() => {
    if (!effectiveWorkspaceId) {
      return;
    }

    const store = workspaceContextStore.getState();
    if (effectiveWorkspace) {
      if (store.workspaceId !== effectiveWorkspace.id || store.rootFolderId !== effectiveWorkspace.rootFolderId) {
        store.setWorkspaceRef({
          workspaceId: effectiveWorkspace.id,
          rootFolderId: effectiveWorkspace.rootFolderId,
        });
      }
      return;
    }

    if (store.workspaceId !== effectiveWorkspaceId) {
      store.setWorkspaceId(effectiveWorkspaceId);
    }
  }, [effectiveWorkspace, effectiveWorkspaceId]);

  const rootBreadcrumbId = effectiveWorkspace?.rootFolderId || ROOT_FOLDER_ID;
  const rootBreadcrumbName = effectiveWorkspace?.name || PRIVATE_WORKSPACE_ROOT_FOLDER_NAME;
  const interpolationRootFolderId = effectiveWorkspace?.rootFolderId || currentProject.parentFolders?.[0]?.id || '';

  const openWorkspaceFolderInNewTab = useCallback(
    (folderId?: string, activeTabIndex?: string) => {
      const url = new URL('/workspaces', window.location.origin);
      if (activeTabIndex) {
        url.searchParams.set('activeTabIndex', activeTabIndex);
      }
      if (effectiveWorkspaceId) {
        url.searchParams.set('workspaceId', effectiveWorkspaceId);
      }
      const nextFolderId = (folderId ?? '').trim();
      const isRootFolder = nextFolderId === ROOT_FOLDER_ID || nextFolderId === rootBreadcrumbId;
      if (nextFolderId && !isRootFolder) {
        url.searchParams.set('folderId', nextFolderId);
      }
      navigateToProject(`${url.pathname}${url.search}`, getAuthPath);
    },
    [effectiveWorkspaceId, getAuthPath, rootBreadcrumbId],
  );

  const folderBreadcrumb = useMemo(() => {
    const isRootFolder = (folderId: string) => folderId === ROOT_FOLDER_ID || folderId === rootBreadcrumbId;

    if (isRootFolder(currentProjectFolderId)) {
      return [{ id: rootBreadcrumbId, name: rootBreadcrumbName }];
    }

    const meta = folderMetadataQuery.data;
    if (meta) {
      return [
        { id: rootBreadcrumbId, name: rootBreadcrumbName },
        ...meta.parentFolders
          .filter((folder) => !isRootFolder(folder.id))
          .map((folder) => ({ id: folder.id, name: folder.name })),
        { id: meta.id, name: meta.name },
      ];
    }

    const rawFolders = currentProject.parentFolders ?? [];
    const hasRoot = rawFolders.some((folder) => isRootFolder(folder.id) || folder.name === rootBreadcrumbName);
    const folders = hasRoot
      ? rawFolders
      : [{ id: rootBreadcrumbId, name: rootBreadcrumbName, parentFolderId: null }, ...rawFolders];
    return folders.map((folder) => ({ id: folder.id, name: folder.name }));
  }, [
    currentProject.parentFolders,
    currentProjectFolderId,
    folderMetadataQuery.data,
    rootBreadcrumbId,
    rootBreadcrumbName,
  ]);

  const handleDraftCreated = useCallback(
    (draft: ProjectItem, context: DraftCreatedContext) => {
      if (onDraftCreated) {
        onDraftCreated(draft, context);
        return;
      }
      // Default behavior: update draft list only
      queryClient.setQueryData(['project-tree-draft-list', currentProject.id], (oldData: unknown) => {
        const oldRecords = (oldData as { records?: ProjectItem[] } | undefined)?.records ?? [];
        return {
          records: [draft, ...oldRecords],
        };
      });
      setActiveDraftId(draft.id);
      queryClient.invalidateQueries({ queryKey: ['project-tree-draft-list', currentProject.id] });

      // Angular parity: auto-open the new draft and close drawers when a view handler is available.
      if (onViewEvent) {
        setDetailsDrawerVisible(false);
        setDetailsDrawerNode(null);
        const modifier = context.modifierPressed ?? context.openInNewTab ?? false;
        const preferNewTab = usePreferenceStore.getState().openInNewTab();
        const shouldOpenNewTab = modifier ? !preferNewTab : preferNewTab;
        if (shouldOpenNewTab) {
          openWorkbenchItemInNewTab(currentProject.id, draft.id, draft.type, modifier);
          onVisibleChange(false);
          return;
        }
        onViewEvent({ item: draft, event: undefined, modifierPressed: modifier });
        onVisibleChange(false);
      }
    },
    [currentProject.id, onDraftCreated, onViewEvent, onVisibleChange, openWorkbenchItemInNewTab, queryClient],
  );

  const openInterItemSelectModal = useCallback((node: ProjectTreeNode) => {
    setInterOperationNode(node);
    setInterItemSelectModalVisible(true);
  }, []);

  const closeInterItemSelectModal = useCallback(() => {
    setInterOperationNode(null);
    setInterItemSelectModalVisible(false);
    setInterItemSelectModalLoading(false);
  }, []);

  const handleInterItemSelectSubmit = useCallback(
    async (selectedId: string, volumeProjectId?: string) => {
      if (!interOperationNode) return;
      const modifierPressed = captureModifierState();
      setInterItemSelectModalLoading(true);
      try {
        const mode = interOperationNode.itemType === 'VolumeMesh' ? 'VolumeMesh' : 'Case';
        const name = interOperationNode.name.replace(/^(Case_|SurfaceMesh_|VolumeMesh_)/, '');

        if (mode === 'VolumeMesh') {
          // VolumeMesh mode: selecting a case to fetch solution from
          const interData = { interpolationCaseId: selectedId, interpolationVolumeMeshId: interOperationNode.id };
          const newDraft = await projectApi.createDraft({
            name: `${name} (interpolation)`,
            sourceItemId: interOperationNode.id,
            sourceItemType: (interOperationNode.itemType || interOperationNode.type) as WorkspaceItemType,
            projectId: currentProject.id,
            solverVersion: interOperationNode.solverVersion,
            forkCase: true,
            ...interData,
          });
          messageApi.success('Interpolation draft created successfully');
          handleDraftCreated(newDraft, { action: 'interpolation', node: interOperationNode, modifierPressed });
          queryClient.invalidateQueries({ queryKey: ['project-tree-items', currentProject.id] });
        } else {
          // Case mode: selecting a volume mesh to inject results into
          const isCrossProject = !!volumeProjectId && volumeProjectId !== currentProject.id;
          const interData = {
            interpolationCaseId: interOperationNode.id,
            interpolationVolumeMeshId: selectedId,
          };
          const targetProjectId = isCrossProject ? volumeProjectId : currentProject.id;
          const newDraft = await projectApi.createDraft({
            name: `${name} (interpolation)`,
            sourceItemId: interOperationNode.id,
            sourceItemType: (interOperationNode.itemType || interOperationNode.type) as WorkspaceItemType,
            projectId: targetProjectId,
            solverVersion: interOperationNode.solverVersion,
            forkCase: true,
            ...interData,
          });
          if (isCrossProject) {
            openWorkbenchItemInNewTab(volumeProjectId, newDraft.id, 'Draft');
          } else {
            messageApi.success('Interpolation draft created successfully');
            handleDraftCreated(newDraft, { action: 'interpolation', node: interOperationNode, modifierPressed });
            queryClient.invalidateQueries({ queryKey: ['project-tree-items', currentProject.id] });
          }
        }
        closeInterItemSelectModal();
      } catch (error) {
        messageApi.error('Failed to create interpolation draft');
        console.error('Interpolation create draft error:', error);
      } finally {
        setInterItemSelectModalLoading(false);
      }
    },
    [
      closeInterItemSelectModal,
      currentProject.id,
      handleDraftCreated,
      interOperationNode,
      messageApi,
      openWorkbenchItemInNewTab,
      queryClient,
    ],
  );

  // Draft list state
  const [activeDraftId, setActiveDraftId] = useState<string>(
    syncsWithCurrentItem ? currentProject.lastOpenDraftId || '' : '',
  );
  const [hoverDraftId, setHoverDraftId] = useState<string>('');
  const [selectedDraftIds, setSelectedDraftIds] = useState<string[]>([]);
  const currentWorkbenchDraftId = effectiveCurrentItem?.type === 'Draft' ? effectiveCurrentItem.id : '';
  // Tree node selection (for compare/batch delete)
  const [checkedNodeIds, setCheckedNodeIds] = useState<string[]>([]);
  const [renameDraftVisible, setRenameDraftVisible] = useState(false);
  const [renameDraftLoading, setRenameDraftLoading] = useState(false);
  const [renameDraftTargetId, setRenameDraftTargetId] = useState<string>('');
  const [renameDraftName, setRenameDraftName] = useState<string>('');
  const [editItemVisible, setEditItemVisible] = useState(false);
  const [editItemTargetNode, setEditItemTargetNode] = useState<ProjectTreeNode | null>(null);
  const [batchDeleteModalOpen, setBatchDeleteModalOpen] = useState(false);
  const [batchDeleteModalNodes, setBatchDeleteModalNodes] = useState<ProjectTreeNode[]>([]);
  const [batchDeleteModalAssets, setBatchDeleteModalAssets] = useState<BatchDeleteAsset[]>([]);
  const wsUnwatchesRef = useRef<Array<() => void>>([]);

  const initialActiveDraftId = useMemo(() => {
    if (!syncsWithCurrentItem) return '';
    return currentWorkbenchDraftId || currentProject.lastOpenDraftId || '';
  }, [currentProject.lastOpenDraftId, currentWorkbenchDraftId, syncsWithCurrentItem]);

  const closeDrawerTransientUi = useCallback(() => {
    setContextMenuVisible(false);
    setContextMenuPosition(DEFAULT_MENU_POSITION);
    setContextMenuNode(null);
    setInterContextMenuVisible(false);
    setInterContextMenuPosition(DEFAULT_MENU_POSITION);
    setInterContextMenuNode(null);
    setDetailsDrawerVisible(false);
    setDetailsDrawerNode(null);
    setNewProjectModalVisible(false);
    setNewProjectCloneNode(null);
    setInterItemSelectModalVisible(false);
    setInterItemSelectModalLoading(false);
    setInterOperationNode(null);
    setRenameDraftVisible(false);
    setRenameDraftLoading(false);
    setRenameDraftTargetId('');
    setRenameDraftName('');
    setEditItemVisible(false);
    setEditItemTargetNode(null);
    setBatchDeleteModalOpen(false);
    setBatchDeleteModalNodes([]);
    setBatchDeleteModalAssets([]);
  }, []);

  const resetDrawerSessionState = useCallback(() => {
    setActiveDraftId(initialActiveDraftId);
    setHoverDraftId('');
    setSelectedDraftIds([]);
    setCheckedNodeIds([]);
  }, [initialActiveDraftId]);

  /**
   * Collect a subtree of ids based on the latest fetched flat list (`parentNodeId`).
   *
   * Why: selected/batch-delete nodes may come from `treeItemMapRef` (flat records) and won't
   * carry `children`, so relying on `node.children` would miss descendants and leave orphan nodes.
   */
  const collectItemIdsFromFlatTree = useCallback((rootIds: string[]): Set<string> => {
    const map = treeItemMapRef.current;
    const childrenByParentId = new Map<string, string[]>();
    map.forEach((node) => {
      const parentId = node.parentNodeId ?? '';
      if (!parentId) return;
      const list = childrenByParentId.get(parentId);
      if (list) {
        list.push(node.id);
      } else {
        childrenByParentId.set(parentId, [node.id]);
      }
    });

    const ids = new Set<string>();
    const stack = [...rootIds];
    while (stack.length) {
      const id = stack.pop();
      if (!id || ids.has(id)) continue;
      ids.add(id);
      const children = childrenByParentId.get(id);
      if (children?.length) {
        stack.push(...children);
      }
    }
    return ids;
  }, []);

  const openBatchDeleteModal = useCallback((nodes: ProjectTreeNode[]) => {
    setBatchDeleteModalNodes(nodes);
    setBatchDeleteModalAssets(
      nodes.map((n) => ({
        id: n.id,
        name: n.name,
        type: (n.itemType || n.type) as unknown as WorkspaceItemTypeFromWorkspace,
      })),
    );
    setBatchDeleteModalOpen(true);
  }, []);

  const clearWsWatches = useCallback(() => {
    if (wsUnwatchesRef.current.length > 0) {
      wsUnwatchesRef.current.forEach((unwatch) => unwatch());
      wsUnwatchesRef.current = [];
    }
  }, []);

  const clearCheckedNodeList = useCallback(() => {
    checkedNodeIds.forEach((id) => {
      updateGraphItem(id, { checked: false }, 'checked');
    });
    setCheckedNodeIds((prev) => (prev.length === 0 ? prev : []));
  }, [checkedNodeIds, updateGraphItem]);

  const compareCases = useCallback(() => {
    const map = treeItemMapRef.current;
    const caseIds = checkedNodeIds
      .map((id) => map.get(id))
      .filter((n): n is ProjectTreeNode => !!n && (n.itemType || n.type) === 'Case')
      .map((n) => n.id);
    if (caseIds.length < 2) return;
    navigateToProject(`/cases/compare_new?casesIds=${caseIds.join('_')}`, getAuthPath);
  }, [checkedNodeIds, getAuthPath]);

  const checkItem = useCallback(
    (item: Item | null, checked?: boolean) => {
      if (!item) return;
      const nodeModel = item.getModel() as unknown as ProjectTreeNode;
      // cannot check root node
      if (!nodeModel.parentNodeId) return;
      // Angular parity: Geometry nodes are not checkable (multi-geometry injects a parentNodeId).
      if (nodeModel.itemType === 'Geometry') return;
      const nextChecked = checked ?? !nodeModel.checked;
      updateGraphItem(item, { checked: nextChecked }, 'checked');
      setCheckedNodeIds((prev) => {
        if (nextChecked) {
          if (prev.includes(nodeModel.id)) return prev;
          return [...prev, nodeModel.id];
        }
        return prev.filter((id) => id !== nodeModel.id);
      });
    },
    [updateGraphItem],
  );

  // Recursively set collapsed state for all children
  const recursiveSetCollapsed = useCallback((node: ProjectTreeNode, collapsed: boolean) => {
    node.collapsed = collapsed;
    node.children?.forEach((childNode) => recursiveSetCollapsed(childNode, collapsed));
  }, []);

  // Handle delete node
  const handleDeleteNode = useCallback(
    async (node: ProjectTreeNode) => {
      if (!graphRef.current) return;

      // Delete via API
      await projectApi.deleteItem(node.id, node.itemType || node.type);

      // Remove from graph
      const ids = collectItemIdsFromFlatTree([node.id]);
      ids.forEach((id) => {
        graphRef.current?.removeItem(id);
      });

      // Update parent node's children if exists
      const parentNodeId = node.parentNodeId;
      if (parentNodeId) {
        const parentItem = graphRef.current.findById(parentNodeId);
        if (parentItem) {
          const parentModel = parentItem.getModel() as unknown as ProjectTreeNode;
          const newChildren = parentModel.children?.filter((item) => item.id !== node.id);
          parentModel.children = newChildren;

          // Update parent node in graph
          updateGraphItem(parentItem, { children: newChildren }, 'default');

          // Sync children in G6 internal state
          const children = parentItem.get('children');
          if (Array.isArray(children)) {
            parentItem.set(
              'children',
              children.filter((item: unknown) => !(item as { destroyed?: boolean }).destroyed),
            );
          }
        }
      }

      // Refresh graph
      graphRef.current.refresh();
    },
    [collectItemIdsFromFlatTree, updateGraphItem],
  );

  const deleteLeafNode = useCallback(
    (node: ProjectTreeNode) => {
      const ids = collectItemIdsFromFlatTree([node.id]);
      const currentViewingId = currentItemAssetIdRef.current;

      const showError = (error: unknown) => {
        const fallback = 'Delete error, please try again or contact admin';
        const maybeErr = error as {
          response?: { data?: { error?: string } };
          message?: string;
        };
        const msg = maybeErr?.response?.data?.error ?? fallback;
        messageApi.error(msg);
      };

      if (currentViewingId && ids.has(currentViewingId)) {
        modalApi.confirm({
          title: 'Delete asset',
          content: (
            <div>
              <strong>The asset is being viewed in workbench and deletion will update the view.</strong>
              <div>
                You are about to delete the asset “<strong>{node.name}</strong>”.
                <br />
                Deletion is not reversible.
              </div>
            </div>
          ),
          okText: 'Delete asset',
          okType: 'primary',
          okButtonProps: { danger: true },
          cancelText: 'Cancel',
          onOk: async () => {
            try {
              await handleDeleteNode(node);
              queryClient.invalidateQueries({ queryKey: ['project-tree-items', currentProject.id] });
              queryClient.invalidateQueries({ queryKey: ['project-tree-draft-list', currentProject.id] });
              messageApi.success('Asset deleted');
              // Align with Angular: go to closest parent if current viewing item is deleted.
              switchToClosestParentOrRoot(ids);
              setCheckedNodeIds((prev) => prev.filter((id) => !ids.has(id)));
            } catch (error) {
              showError(error);
            }
          },
        });
      } else {
        modalApi.confirm({
          title: 'Delete asset',
          content: (
            <div>
              You are about to delete the asset “<strong>{node.name}</strong>”.
              <br />
              Deletion is not reversible.
            </div>
          ),
          okText: 'Delete asset',
          okType: 'primary',
          okButtonProps: { danger: true },
          cancelText: 'Cancel',
          onOk: async () => {
            try {
              await handleDeleteNode(node);
              queryClient.invalidateQueries({ queryKey: ['project-tree-items', currentProject.id] });
              queryClient.invalidateQueries({ queryKey: ['project-tree-draft-list', currentProject.id] });
              messageApi.success('Asset deleted');
              setCheckedNodeIds((prev) => prev.filter((id) => !ids.has(id)));
            } catch (error) {
              showError(error);
            }
          },
        });
      }
    },
    [
      collectItemIdsFromFlatTree,
      currentProject.id,
      handleDeleteNode,
      messageApi,
      modalApi,
      queryClient,
      switchToClosestParentOrRoot,
    ],
  );

  const showCrossProjectDependencyWarning = useCallback(
    (
      nodeList: ProjectTreeNode[],
      deps: { itemId: string; itemName: string; itemType: string; projectId: string; projectName: string }[],
    ) => {
      const itemDesc =
        nodeList.length === 1
          ? `This ${nodeList[0].itemType === 'VolumeMesh' ? 'volume mesh' : 'case'}`
          : 'The selected items';
      const depList = deps.map((d) => `• Case "${d.itemName}" in project "${d.projectName}"`).join('\n');
      modalApi.confirm({
        title: 'Cross-project dependency detected',
        content: `${itemDesc} is used as the interpolation parent of:\n${depList}\nDeleting will break that dependency.`,
        okText: 'Delete anyway',
        okType: 'primary',
        okButtonProps: { danger: true },
        onOk: async () => {
          if (nodeList.length === 1 && !nodeList[0].children?.length) {
            await deleteLeafNode(nodeList[0]);
          } else {
            openBatchDeleteModal(nodeList);
          }
        },
        cancelText: 'Cancel',
      });
    },
    [deleteLeafNode, modalApi, openBatchDeleteModal],
  );

  const checkCrossProjectDependencies = useCallback(
    async (nodes: ProjectTreeNode[]): Promise<boolean> => {
      const nodesToCheck = nodes.filter((n) => n.itemType === 'VolumeMesh' || n.itemType === 'Case');
      if (!nodesToCheck.length) return false;
      try {
        const allDeps: {
          itemId: string;
          itemName: string;
          itemType: string;
          projectId: string;
          projectName: string;
        }[] = [];
        await Promise.all(
          nodesToCheck.map(async (node) => {
            const result = await projectApi.getCrossProjectDependencies({
              projectId: currentProject.id,
              itemId: node.id,
              itemType: node.itemType || (node.type as string),
            });
            if (result?.records?.length) {
              allDeps.push(...result.records);
            }
          }),
        );
        if (allDeps.length) {
          const uniqueDeps = [...new Map(allDeps.map((d) => [d.itemId, d])).values()];
          showCrossProjectDependencyWarning(nodes, uniqueDeps);
          return true;
        }
      } catch {
        // If the check fails, proceed with normal delete flow
      }
      return false;
    },
    [currentProject.id, showCrossProjectDependencyWarning],
  );

  const beforeDeleteNode = useCallback(
    async (node: ProjectTreeNode) => {
      if (!graphRef.current) return;
      if (node.id === currentProject.rootItemId) {
        messageApi.warning('The root asset cannot be deleted');
        return;
      }
      // Check cross-project interpolation dependencies for VolumeMesh and Case
      if (node.itemType === 'VolumeMesh' || node.itemType === 'Case') {
        const hasDeps = await checkCrossProjectDependencies([node]);
        if (hasDeps) return;
      }
      if (node.children?.length) {
        openBatchDeleteModal([node]);
      } else {
        deleteLeafNode(node);
      }
    },
    [checkCrossProjectDependencies, currentProject.rootItemId, deleteLeafNode, messageApi, openBatchDeleteModal],
  );

  const batchDelete = useCallback(async () => {
    const map = treeItemMapRef.current;
    const nodes = checkedNodeIds.map((id) => map.get(id)).filter((n): n is ProjectTreeNode => !!n);
    if (nodes.length === 0) return;
    const hasDeps = await checkCrossProjectDependencies(nodes);
    if (hasDeps) return;
    openBatchDeleteModal(nodes);
  }, [checkedNodeIds, checkCrossProjectDependencies, openBatchDeleteModal]);

  const updateItemDataBeforeShowContextMenu = useCallback(
    (item: Item) => {
      const nodeModel = item.getModel() as unknown as ProjectTreeNode;
      const map = treeItemMapRef.current;
      const latest = map.get(nodeModel.id);
      if (!latest) return;

      // keep dynamic fields from current model (ws-driven)
      // Intentionally omit progress/displayStatus/currentProgress to avoid overriding ws-updated fields.
      const {
        progress: _progress,
        displayStatus: _displayStatus,
        currentProgress: _currentProgress,
        // IMPORTANT: keep G6 node type ('flow-rect') and map backend business type -> itemType
        type: latestType,
        itemType: latestItemType,
        ...restLatest
      } = latest as unknown as Record<string, unknown>;
      void _progress;
      void _displayStatus;
      void _currentProgress;

      // update interpolation fields before menu
      const merged: ProjectTreeNode = {
        ...nodeModel,
        ...(restLatest as unknown as ProjectTreeNode),
        type: nodeModel.type, // preserve G6 node type
        itemType: (nodeModel.itemType ??
          (latestItemType as ProjectTreeNode['itemType']) ??
          (latestType as ProjectTreeNode['itemType'])) as ProjectTreeNode['itemType'],
      };
      setupInterItemFieldsRef.current(merged);

      // If inter item is in same project, refresh its name; otherwise keep outer project fields.
      if (merged.interItemId && !merged.isInterRefOuterProject) {
        const inter = map.get(merged.interItemId);
        if (inter) {
          merged.interItemName = inter.name;
        } else {
          merged.interItemDeleted = true;
        }
      }

      updateGraphItem(item, merged, 'default');
    },
    [updateGraphItem],
  );

  // WebSocket incremental updates (progress/status) - align with Angular initWs().
  // Stays subscribed for the component lifetime (Angular parity: takeUntilDestroyed).
  // The handler safely no-ops when graphRef.current is null.
  useEffect(() => {
    interface WsPercentage {
      Id: string;
      Percentage: number;
      Time?: string;
      [key: string]: unknown;
    }
    interface ProgressUpdateMessage {
      type?: string;
      data?: WsPercentage;
    }
    interface WsStatusUpdate {
      eventType?: string;
      resourceId?: string;
      currentStatus?: string;
      [key: string]: unknown;
    }

    const unsubscribe = onMessage(({ data }) => {
      const socketData = data as { source?: string; type?: string; data?: unknown };

      if ((socketData as ProgressUpdateMessage)?.type === 'progress.update') {
        const payload = (socketData as ProgressUpdateMessage).data;
        if (!payload?.Id) return;
        const nextProgress = typeof payload.Percentage === 'number' ? payload.Percentage : 0;
        useResourceProgressStore.getState().setProgress(payload.Id, nextProgress);

        const graph = graphRef.current;
        if (!graph) return;
        const node = graph.findById(payload.Id);
        if (!node) return;
        const nodeModel = node.getModel() as unknown as ProjectTreeNode;
        if (isDisplayStatusFinished(nodeModel.displayStatus as unknown as DisplayStatus | undefined)) {
          return;
        }

        updateGraphItem(
          node,
          {
            progress: nextProgress,
            solverUpdatedAt: payload.Time || new Date().toISOString(),
          },
          'progress',
        );
        return;
      }

      if (!graphRef.current) return;

      if (socketData.source === 'flow360' && socketData.type === 'resource_event') {
        let payload: WsStatusUpdate | null = null;
        try {
          if (typeof socketData.data === 'string') payload = JSON.parse(socketData.data) as WsStatusUpdate;
          else payload = (socketData.data as WsStatusUpdate) ?? null;
        } catch {
          payload = null;
        }
        if (!payload?.eventType || !payload.resourceId) return;
        if (
          payload.eventType === 'resource_status_change' ||
          payload.eventType === 'resource_post_process_status_change' ||
          payload.eventType === 'resource_visualization_status_change'
        ) {
          const node = graphRef.current.findById(payload.resourceId);
          if (!node) return;
          const nodeModel = node.getModel() as unknown as ProjectTreeNode;
          if (isDisplayStatusFinished(nodeModel.displayStatus as unknown as DisplayStatus | undefined)) {
            return;
          }

          const nextDisplayStatus =
            getDisplayStatusFromLegacyStatus({
              type: (nodeModel.itemType || nodeModel.type) as string,
              status: payload.currentStatus,
            }) ?? (nodeModel.displayStatus as unknown as DisplayStatus | undefined);

          updateGraphItem(node, { displayStatus: nextDisplayStatus }, 'default');
        }
      }
    });

    return unsubscribe;
  }, [onMessage, updateGraphItem]);

  // Fetch draft list
  const {
    data: draftListData,
    isLoading: isDraftListLoading,
    refetch: refetchDraftList,
  } = useQuery({
    queryKey: ['project-tree-draft-list', currentProject.id],
    queryFn: async () => {
      if (!visible || viewMode) {
        return { records: [] };
      }
      const res = await projectApi.getDrafts(currentProject.id);
      const records = (res as unknown as { records?: ProjectItem[] }).records ?? [];
      return { records };
    },
    enabled: visible && !viewMode,
  });

  const {
    data: treeItemsData,
    isLoading,
    refetch: refetchTreeItems,
  } = useQuery({
    queryKey: ['project-tree-items', currentProject.id],
    queryFn: async () => {
      const data = await projectApi.getProjectTreeItems(currentProject.id);
      // Transform data: calculate parentNodeId based on business logic
      // parentNodeId = isVolumeInter ? parentId : (parentCaseId ?? parentId)
      const records = data.records
        .filter((item: unknown) => (item as { type?: string }).type !== 'Folder')
        .map((item: unknown) => {
          const itemData = item as {
            companionParentItemType?: string;
            currentProgress?: ProjectItem['currentProgress'];
            interpolated?: boolean;
            parentId?: string;
            parentCaseId?: string;
            parentCaseName?: string;
            parentCaseDeleted?: boolean;
            parentCaseProjectId?: string;
            parentCaseProjectName?: string;
            parentItemName?: string;
            parentItemDeleted?: boolean;
            parentItemProjectId?: string;
            parentItemProjectName?: string;
            projectId?: string;
            type?: string;
            id?: string;
            name?: string;
            [key: string]: unknown;
          };
          const isVolumeInter = itemData.companionParentItemType === 'VolumeMesh' && itemData.interpolated;
          return {
            ...itemData,
            parentId: itemData.parentId,
            isVolumeInter,
            parentNodeId: itemData.parentCaseId ?? itemData.parentId,
            progress: getProjectTreeProgress(itemData),
            projectId: itemData.projectId || currentProject.id,
          } as unknown as ProjectTreeNode;
        });

      // Angular parity: if there are multiple Geometry roots, inject a mock root node so the tree becomes single-root.
      const geometryCount = records.filter((r) => (r as unknown as { type?: string }).type === 'Geometry').length;
      const hasMultiGeometry = geometryCount > 1;
      const finalRecords = hasMultiGeometry
        ? [
            {
              id: MOCK_ROOT_NODE_ID,
              name: currentProject.name,
              type: currentProject.rootItemType ?? 'Geometry',
              parentNodeId: null,
              projectId: currentProject.id,
            } as unknown as ProjectTreeNode,
            ...records.map((r) => {
              const type = (r as unknown as { type?: string }).type;
              if (type !== 'Geometry') return r;
              return {
                ...r,
                parentNodeId: MOCK_ROOT_NODE_ID,
              } as ProjectTreeNode;
            }),
          ]
        : records;

      return {
        ...data,
        hasMultiGeometry,
        records: finalRecords,
      } as typeof data & { hasMultiGeometry: boolean; records: ProjectTreeNode[] };
    },
    enabled: visible, // Only fetch when drawer is visible
  });

  const hasMultiGeometry = useMemo(() => {
    return Boolean((treeItemsData as unknown as { hasMultiGeometry?: boolean } | undefined)?.hasMultiGeometry);
  }, [treeItemsData]);

  const wasVisibleRef = useRef(false);
  useEffect(() => {
    const wasVisible = wasVisibleRef.current;
    wasVisibleRef.current = visible;
    if (!visible || wasVisible) return;

    closeDrawerTransientUi();
    resetDrawerSessionState();

    // When opening the drawer, always refetch to ensure latest tree + draft list.
    void refetchTreeItems();
    if (!viewMode) {
      void refetchDraftList();
    }
  }, [closeDrawerTransientUi, refetchDraftList, refetchTreeItems, resetDrawerSessionState, viewMode, visible]);

  const treeItemMap = useMemo(() => {
    const records = (treeItemsData as unknown as { records?: ProjectTreeNode[] } | undefined)?.records ?? [];
    return new Map<string, ProjectTreeNode>(records.map((r) => [r.id, r]));
  }, [treeItemsData]);

  useEffect(() => {
    treeItemMapRef.current = treeItemMap;
  }, [treeItemMap]);

  // Keep checked nodes in sync with latest fetched items (drop deleted ids)
  useEffect(() => {
    if (checkedNodeIds.length === 0) return;
    const next = checkedNodeIds.filter((id) => treeItemMap.has(id));
    if (next.length !== checkedNodeIds.length) {
      setCheckedNodeIds(next);
    }
  }, [checkedNodeIds, treeItemMap]);

  const interItemSelectData = useMemo(() => {
    const records = (treeItemsData as unknown as { records?: ProjectTreeNode[] } | undefined)?.records ?? [];
    return records
      .filter((item) => item.id !== MOCK_ROOT_NODE_ID)
      .map((item) => ({
        id: item.id,
        type: item.type,
        name: item.name,
        parentId: item.parentId,
      }));
  }, [treeItemsData]);

  // Setup interpolation item fields - needs to be accessible in event handlers
  const setupInterItemFields = useCallback(
    (node: ProjectTreeNode) => {
      const currentProjectId = currentProject.id;
      const isPlacedUnderCase = node.parentNodeId !== node.parentId;
      const isOuterProject = node.projectId !== currentProjectId;

      if (node.isVolumeInter && !isPlacedUnderCase) {
        // Volume interpolation placed under its VM → inter item is the case (case inserted from elsewhere)
        const nodeData = node as unknown as {
          parentCaseId?: string;
          parentCaseName?: string;
          parentCaseDeleted?: boolean;
          parentCaseProjectId?: string;
          parentCaseProjectName?: string;
        };
        node.interItemId = nodeData.parentCaseId;
        node.interItemType = 'Case';
        node.interItemName = nodeData.parentCaseName;
        node.interItemDeleted = nodeData.parentCaseDeleted;
        node.interItemProjectId = nodeData.parentCaseProjectId;
        node.interItemProjectName = nodeData.parentCaseProjectName;
        node.isInterItemOuterProject =
          isOuterProject || (!!nodeData.parentCaseProjectId && nodeData.parentCaseProjectId !== currentProjectId);
        node.isInterRefOuterProject =
          !!nodeData.parentCaseProjectId && nodeData.parentCaseProjectId !== currentProjectId;
      } else if (node.isCaseInter || (node.isVolumeInter && isPlacedUnderCase)) {
        // Case interpolation OR volume interpolation placed under case → inter item is the VM (VM inserted from elsewhere)
        const nodeData = node as unknown as {
          parentId?: string;
          parentItemName?: string;
          parentItemDeleted?: boolean;
          parentItemProjectId?: string;
          parentItemProjectName?: string;
        };
        node.interItemId = nodeData.parentId;
        node.interItemType = 'VolumeMesh';
        node.interItemName = nodeData.parentItemName;
        node.interItemDeleted = nodeData.parentItemDeleted;
        node.interItemProjectId = nodeData.parentItemProjectId;
        node.interItemProjectName = nodeData.parentItemProjectName;
        node.isInterItemOuterProject =
          isOuterProject || (!!nodeData.parentItemProjectId && nodeData.parentItemProjectId !== currentProjectId);
        node.isInterRefOuterProject =
          !!nodeData.parentItemProjectId && nodeData.parentItemProjectId !== currentProjectId;
      }
    },
    [currentProject.id],
  );

  useEffect(() => {
    setupInterItemFieldsRef.current = setupInterItemFields;
  }, [setupInterItemFields]);

  // Build tree structure using buildTree utility
  const treeData = useMemo<ProjectTreeNode | null>(() => {
    if (!treeItemsData?.records || treeItemsData.records.length === 0) {
      return null;
    }

    const currentProjectId = currentProject.id;
    const items = (treeItemsData.records as unknown as ProjectTreeNode[]).map((item) => {
      const itemData = item as unknown as {
        type?: string;
        isVolumeInter?: boolean;
        parentItemProjectId?: string;
        parentId?: string;
        parentNodeId?: string | null;
      };
      if (itemData.type === 'Case' && itemData.isVolumeInter && itemData.parentItemProjectId === currentProjectId) {
        itemData.parentNodeId = itemData.parentId;
      }
      return item;
    });

    const tree = buildTree<ProjectTreeNode, ProjectTreeNode>({
      nodeList: items as unknown as ProjectTreeNode[],
      parentIdFieldName: 'parentNodeId',
      rootId: null,
      needSort: true,
      compareFn: (a, b) => {
        // Angular parity:
        // - Geometry nodes: sort by createdAt asc
        // - Others: sort by createdAt desc
        const aItemType = (a as unknown as { itemType?: string }).itemType;
        const bItemType = (b as unknown as { itemType?: string }).itemType;
        const aDate = (a as unknown as { createdAt?: string }).createdAt;
        const bDate = (b as unknown as { createdAt?: string }).createdAt;
        const aTime = aDate ? new Date(aDate).valueOf() : 0;
        const bTime = bDate ? new Date(bDate).valueOf() : 0;
        if (aItemType === 'Geometry' && bItemType === 'Geometry') {
          return aTime - bTime;
        }
        return bTime - aTime;
      },
      mapFn: (item) => {
        const { type, ...rest } = item;
        // Ensure parentNodeId is preserved
        // itemType = business type (Geometry/SurfaceMesh/Case/VolumeMesh)
        // type = G6 node type ('flow-rect')
        // Current-node/highlight styling is synced separately so viewport stays stable.
        return {
          ...rest,
          itemType: type as ProjectTreeNode['itemType'], // Business type: Geometry, SurfaceMesh, etc.
          type: 'flow-rect' as unknown as ProjectTreeNode['type'], // G6 node type, cast to avoid type conflict
          projectId: (item as unknown as { projectId?: string }).projectId || currentProject.id, // Ensure projectId is set
          parentNodeId: (item as unknown as { parentNodeId?: string | null }).parentNodeId, // Explicitly preserve parentNodeId
          isCurrent: false,
          collapsed: false, // Default to expanded
          highlight: false,
        } as ProjectTreeNode;
      },
    });

    // Compute current viewing item path (Angular parity: expand ancestor path to current item)
    const currentAssetId = currentItemAssetIdRef.current;
    const currentViewingItemPath = new Set<string>();
    if (currentAssetId) {
      const itemMap = new Map<string, ProjectTreeNode>();
      items.forEach((item) => itemMap.set(item.id, item));
      let pathItem: ProjectTreeNode | undefined = itemMap.get(currentAssetId);
      while (pathItem) {
        currentViewingItemPath.add(pathItem.id);
        const pnId = (pathItem as unknown as { parentNodeId?: string | null }).parentNodeId;
        pathItem = pnId ? itemMap.get(pnId) : undefined;
      }
    }

    // Preprocess data: set collapsed state and interpolation fields
    const preprocessData = (node: ProjectTreeNode, level = 1, maxCount = 10, maxLevel = 4) => {
      if (!node.parentNodeId || currentViewingItemPath.has(node.id)) {
        node.collapsed = false;
      } else if ((node.children?.length || 0) > maxCount || level >= maxLevel) {
        node.collapsed = true;
      } else {
        node.collapsed = false;
      }

      // Calculate interpolation flags
      const nodeData = node as unknown as {
        parentCaseId?: string;
        parentCaseProjectId?: string;
        parentId?: string;
      };
      node.isCaseInter =
        !!nodeData.parentCaseId &&
        node.itemType === 'Case' &&
        !!nodeData.parentCaseProjectId &&
        nodeData.parentCaseProjectId !== currentProject.id;
      node.isInter = node.isVolumeInter || node.isCaseInter;

      // Setup inter item fields
      setupInterItemFields(node);

      if (node.children) {
        node.children.forEach((child) => preprocessData(child, level + 1, maxCount, maxLevel));
      }
    };

    const rootNode = tree[0];
    if (rootNode) {
      preprocessData(rootNode, 1, 10, hasMultiGeometry ? 5 : 4);
      return rootNode;
    }
    return null;
  }, [treeItemsData, currentProject.id, hasMultiGeometry, setupInterItemFields]);

  // WS watch list maintenance: re-watch running items whenever treeData changes (match Angular behavior)
  useEffect(() => {
    if (!visible || !treeData) {
      clearWsWatches();
      return;
    }

    clearWsWatches();

    const stack: ProjectTreeNode[] = [treeData];
    let current: ProjectTreeNode | undefined;
    while ((current = stack.pop())) {
      if (current.displayStatus === 'running') {
        const watchItem = {
          type: 'progress.update',
          data: {
            id: current.id,
            type: (current.itemType || current.type) as unknown as WorkspaceItemTypeFromWorkspace,
          },
        };
        wsUnwatchesRef.current.push(watchWebSocket(watchItem));
      }
      if (current.children) {
        stack.push(...current.children);
      }
    }
  }, [clearWsWatches, treeData, visible, watchWebSocket]);

  const calculatedItemStats = useMemo(() => {
    const stats = {
      completed: 0,
      running: 0,
      postProcess: 0,
      postProcessFailed: 0,
      error: 0,
      diverged: 0,
    };
    if (!treeItemsData?.records) {
      return stats;
    }
    // Mirrors the visual status semantics used by the project tree node icon
    // (see register-project-node.ts → drawStatusShape) and Flow360StatusIcon:
    // a Case whose displayStatus is "completed" can still be in postprocessing/
    // visualizing (Post process) or have a failed post-process (halfCompleted).
    treeItemsData.records.forEach((item: ProjectItem) => {
      if (item.displayStatus === 'completed') {
        if (item.type === 'Case' && (item.visualizationStatus === 'error' || item.postProcessStatus === 'error')) {
          stats.postProcessFailed++;
        } else if (
          item.type === 'Case' &&
          (item.displayOverallStatus === 'visualizing' || item.displayOverallStatus === 'postprocessing')
        ) {
          stats.postProcess++;
        } else {
          stats.completed++;
        }
      } else if (item.displayStatus === 'error') {
        stats.error++;
      } else if (item.displayStatus === 'diverged') {
        stats.diverged++;
      } else if (item.displayStatus === 'running' || item.displayStatus === 'submitted') {
        stats.running++;
      }
    });
    return stats;
  }, [treeItemsData]);

  const itemStats = calculatedItemStats;

  // Draft list
  const draftRecords = useMemo<ProjectItem[]>(() => {
    if (viewMode) {
      return [];
    }
    return (draftListData?.records || []).filter((d) => {
      const type =
        (d as unknown as { type?: string; itemType?: string }).type ?? (d as unknown as { itemType?: string }).itemType;
      return type === 'Draft';
    });
  }, [draftListData, viewMode]);

  useEffect(() => {
    draftRecordsRef.current = draftRecords;
  }, [draftRecords]);

  const draftList = useMemo<Array<{ id: string; name: string; type: 'Draft' }>>(() => {
    return draftRecords.map((d) => ({ id: d.id, name: d.name, type: 'Draft' }));
  }, [draftRecords]);

  useEffect(() => {
    if (!visible || !syncsWithCurrentItem) return;
    if (draftList.length === 0) return;
    if (activeDraftId && draftList.some((d) => d.id === activeDraftId)) return;
    setActiveDraftId(draftList[0].id);
  }, [activeDraftId, draftList, syncsWithCurrentItem, visible]);

  // Keep draft highlight consistent with current workbench draft (Angular `initDraft()` parity).
  useEffect(() => {
    if (!visible || viewMode || !syncsWithCurrentItem) return;
    if (!currentWorkbenchDraftId) return;
    if (activeDraftId === currentWorkbenchDraftId) return;
    setActiveDraftId(currentWorkbenchDraftId);
  }, [activeDraftId, currentWorkbenchDraftId, syncsWithCurrentItem, viewMode, visible]);

  const getDraftById = useCallback(
    (draftId: string): ProjectItem | undefined => {
      return draftRecords.find((d) => d.id === draftId);
    },
    [draftRecords],
  );

  const activeDraft = useMemo(() => {
    return activeDraftId ? getDraftById(activeDraftId) : undefined;
  }, [activeDraftId, getDraftById]);

  // Hovered draft has higher priority than selected draft for highlighting (match legacy behavior)
  const highlightDraft = useMemo(() => {
    if (hoverDraftId) {
      return getDraftById(hoverDraftId);
    }
    return activeDraft;
  }, [activeDraft, getDraftById, hoverDraftId]);

  const currentItemAssetId = useMemo(() => {
    if (!effectiveCurrentItem) return '';
    if (effectiveCurrentItem.type === 'Draft') {
      const draft = effectiveCurrentItem as unknown as { operationFromItemId?: string; sourceItemId?: string };
      return draft.operationFromItemId || draft.sourceItemId || '';
    }
    return effectiveCurrentItem.id;
  }, [effectiveCurrentItem]);

  const currentTreeNodeId = useMemo(() => {
    if (!effectiveCurrentItem || effectiveCurrentItem.type === 'Draft') {
      return '';
    }
    return effectiveCurrentItem.id;
  }, [effectiveCurrentItem]);

  useEffect(() => {
    currentItemAssetIdRef.current = currentItemAssetId;
  }, [currentItemAssetId]);

  useEffect(() => {
    currentTreeNodeIdRef.current = currentTreeNodeId;
  }, [currentTreeNodeId]);

  const highlightTargetId = useMemo(() => {
    const draft = highlightDraft as unknown as { operationFromItemId?: string; sourceItemId?: string } | undefined;
    // When hovering a draft in the list, highlight that draft's source asset
    if (hoverDraftId) {
      return draft?.operationFromItemId || draft?.sourceItemId || currentItemAssetId || '';
    }
    // Otherwise, prioritize the current workbench item's asset
    return currentItemAssetId || draft?.operationFromItemId || draft?.sourceItemId || '';
  }, [highlightDraft, currentItemAssetId, hoverDraftId]);

  const checkedNodeList = useMemo(() => {
    return checkedNodeIds.map((id) => treeItemMap.get(id)).filter((n): n is ProjectTreeNode => !!n);
  }, [checkedNodeIds, treeItemMap]);

  const compareBtnDisabled = useMemo(() => {
    if (checkedNodeList.length < 2) return true;
    return checkedNodeList.some((n) => (n.itemType || n.type) !== 'Case');
  }, [checkedNodeList]);

  const highlightPathIds = useMemo(() => {
    if (!treeData || !highlightTargetId) return [];

    const path: string[] = [];
    const dfs = (node: ProjectTreeNode): boolean => {
      path.push(node.id);
      if (node.id === highlightTargetId) return true;
      if (node.children) {
        for (const child of node.children) {
          if (dfs(child)) return true;
        }
      }
      path.pop();
      return false;
    };

    dfs(treeData);
    return path;
  }, [treeData, highlightTargetId]);

  useEffect(() => {
    highlightPathIdsRef.current = highlightPathIds;
  }, [highlightPathIds]);

  useEffect(() => {
    if (!visible) return;
    const graph = graphRef.current;
    if (!graph) return;

    syncGraphNodeVisualState(graph, highlightPathIds, currentTreeNodeId);
  }, [visible, highlightPathIds, currentTreeNodeId, syncGraphNodeVisualState]);

  const isDraftRenameDisabled = useCallback(
    (draftId: string) => {
      return isCurrentDraftLocked && draftId === currentWorkbenchDraftId;
    },
    [currentWorkbenchDraftId, isCurrentDraftLocked],
  );

  const isDraftDeleteDisabled = useCallback(
    (draftId: string) => {
      const isLockedCurrentDraft = isCurrentDraftLocked && draftId === currentWorkbenchDraftId;
      return isLockedCurrentDraft || (draftRecords.length <= 1 && draftId === activeDraftId);
    },
    [activeDraftId, currentWorkbenchDraftId, draftRecords.length, isCurrentDraftLocked],
  );

  const handleViewDraftInWorkbench = useCallback(
    (draftId: string, event?: React.MouseEvent) => {
      const draft = getDraftById(draftId);
      if (!draft) return;
      onViewEvent?.({ item: draft, event });
      onVisibleChange(false);
    },
    [getDraftById, onViewEvent, onVisibleChange],
  );

  // Handle active draft change - highlight only (no auto navigation)
  const handleActiveDraftChange = useCallback((draftId: string) => {
    setActiveDraftId(draftId);
  }, []);

  const handleRenameDraft = useCallback(
    (draftId: string) => {
      if (isDraftRenameDisabled(draftId)) {
        return;
      }
      const draft = getDraftById(draftId);
      if (!draft) return;
      setRenameDraftTargetId(draftId);
      setRenameDraftName(draft.name);
      setRenameDraftVisible(true);
    },
    [getDraftById, isDraftRenameDisabled],
  );

  const handleDeleteDraft = useCallback(
    async (draftId: string) => {
      if (isDraftDeleteDisabled(draftId)) {
        return;
      }
      const draft = getDraftById(draftId);
      if (!draft) return;

      // Direct delete (no confirm) - consistent with legacy behavior
      try {
        await projectApi.deleteItem(draftId, 'Draft');

        // Optimistically remove from cache so UI updates immediately
        queryClient.setQueryData(['project-tree-draft-list', currentProject.id], (oldData: unknown) => {
          const oldRecords = (oldData as { records?: ProjectItem[] } | undefined)?.records ?? [];
          return { records: oldRecords.filter((d) => d.id !== draftId) };
        });

        setSelectedDraftIds((prev) => prev.filter((id) => id !== draftId));
        if (activeDraftId === draftId) {
          const nextId = draftRecords.find((d) => d.id !== draftId)?.id ?? '';
          setActiveDraftId(nextId);
        }

        // Align with Angular: if the current draft is deleted, switch to another draft (or root).
        if (effectiveCurrentItem?.type === 'Draft' && effectiveCurrentItem.id === draftId) {
          switchToAnotherDraftOrRoot({ excludeDraftIds: new Set([draftId]) });
        }

        messageApi.success('Draft deleted');
      } catch (error) {
        messageApi.error('Failed to delete draft');
        console.error('Delete draft error:', error);
      } finally {
        queryClient.invalidateQueries({ queryKey: ['project-tree-draft-list', currentProject.id] });
      }
    },
    [
      activeDraftId,
      currentProject.id,
      draftRecords,
      effectiveCurrentItem?.id,
      effectiveCurrentItem?.type,
      getDraftById,
      isDraftDeleteDisabled,
      messageApi,
      queryClient,
      switchToAnotherDraftOrRoot,
    ],
  );

  const handleBatchDeleteDrafts = useCallback(
    (draftIds: string[]) => {
      if (isCurrentDraftLocked && draftIds.includes(currentWorkbenchDraftId)) {
        messageApi.warning('The current draft is locked in another session and cannot be deleted.');
        return;
      }
      const remainingCount = draftRecords.filter((d) => !draftIds.includes(d.id)).length;
      if (remainingCount === 0) {
        messageApi.warning('At least one draft must remain.');
        return;
      }
      modalApi.confirm({
        title: 'Delete drafts',
        content: <div>Delete {draftIds.length} drafts? Deletion is not reversible.</div>,
        okText: 'Delete',
        okButtonProps: { danger: true },
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            await Promise.all(draftIds.map((id) => projectApi.deleteItem(id, 'Draft')));

            queryClient.setQueryData(['project-tree-draft-list', currentProject.id], (oldData: unknown) => {
              const oldRecords = (oldData as { records?: ProjectItem[] } | undefined)?.records ?? [];
              const removeSet = new Set(draftIds);
              return { records: oldRecords.filter((d) => !removeSet.has(d.id)) };
            });

            setSelectedDraftIds([]);
            if (draftIds.includes(activeDraftId)) {
              const nextId = draftRecords.find((d) => !draftIds.includes(d.id))?.id ?? '';
              setActiveDraftId(nextId);
            }

            if (effectiveCurrentItem?.type === 'Draft' && draftIds.includes(effectiveCurrentItem.id)) {
              switchToAnotherDraftOrRoot({ excludeDraftIds: new Set(draftIds) });
            }

            messageApi.success('Drafts deleted');
          } catch (error) {
            messageApi.error('Failed to delete drafts');
            console.error('Batch delete drafts error:', error);
            throw error;
          } finally {
            queryClient.invalidateQueries({ queryKey: ['project-tree-draft-list', currentProject.id] });
          }
        },
      });
    },
    [
      activeDraftId,
      currentProject.id,
      currentWorkbenchDraftId,
      draftRecords,
      effectiveCurrentItem?.id,
      effectiveCurrentItem?.type,
      isCurrentDraftLocked,
      messageApi,
      modalApi,
      queryClient,
      switchToAnotherDraftOrRoot,
    ],
  );

  // Initialize a fresh G6 graph whenever the drawer opens or the tree root changes.
  useEffect(() => {
    if (!visible || !containerRef.current || !treeData || isLoading) {
      return;
    }

    const container = containerRef.current;
    if (graphRef.current) {
      graphRef.current.destroy();
      graphRef.current = null;
    }

    // Hide container until the graph is positioned to prevent flash
    container.style.visibility = 'hidden';

    // Wait for container to have dimensions
    const initGraph = () => {
      const width = Math.max(container.clientWidth || 800, 500);
      const height = Math.max(container.clientHeight || 600, 500);

      const minimap = new Minimap({
        size: [140, 140],
        // Render all shapes to show a real thumbnail.
        // Note: our custom node returns a transparent container as keyShape, so `keyShape` would look empty.
        type: 'default',
        viewportClassName: 'flow360-minimap-viewport',
      });

      const brushSelectBehavior = {
        type: 'brush-select',
        trigger: 'shift',
        includeEdges: false,
        onSelect: (items?: Item[]) => {
          items?.forEach((it) => {
            checkItem(it);
          });
        },
        brushStyle: {
          fill: 'rgba(227, 227, 227, 0.3)',
          stroke: '#a3a3a3',
          lineWidth: 1,
        },
      };

      const graph = new G6.TreeGraph({
        container,
        width,
        height,
        modes: {
          default: [brushSelectBehavior as unknown as { type: string }, 'zoom-canvas', 'drag-canvas'],
        },
        fitView: false,
        fitViewPadding: 20,
        minZoom: 0.5,
        maxZoom: 10,
        animate: true,
        animateCfg: {
          duration: 300,
          easing: 'easePolyInOut',
        },
        defaultNode: {
          type: 'flow-rect',
          size: [270, 40], // Explicitly set node size
        },
        defaultEdge: {
          type: 'polyline',
          style: {
            stroke: Color.edge,
            endArrow: true,
            lineWidth: 2,
          },
          sourceAnchor: 3,
          targetAnchor: 0,
        },
        layout: {
          type: 'indented',
          isHorizontal: true,
          direction: 'LR',
          dropCap: true,
          indent: 150,
          getHeight: () => 20,
          getWidth: () => 270,
        },
        plugins: [minimap],
      });

      graphRef.current = graph;

      // Ensure node visibility - adjust tree position if node is out of viewport
      const ensureNodeVisibility = (id: string) => {
        const node = graph.findById(id);
        if (!node) {
          return;
        }
        const childBBox = node.getCanvasBBox();
        const { minX, minY, maxX, maxY } = childBBox;
        // Get leftTop/rightBottom points in canvas coordinates
        const leftTopPoint = graph.getCanvasByPoint(minX, minY);
        const rightBottomPoint = graph.getCanvasByPoint(maxX, maxY);
        // Reserve some space for min/max xy
        const visibleMinX = 40;
        const visibleMaxX = graph.getWidth() - 300;
        const visibleMinY = 40;
        const visibleMaxY = graph.getHeight() - 80;
        // Calculate dx
        const dx =
          leftTopPoint.x < visibleMinX
            ? visibleMinX - leftTopPoint.x
            : rightBottomPoint.x > visibleMaxX
              ? visibleMaxX - rightBottomPoint.x
              : 0;
        const dy =
          leftTopPoint.y < visibleMinY
            ? visibleMinY - leftTopPoint.y
            : rightBottomPoint.y > visibleMaxY
              ? visibleMaxY - rightBottomPoint.y
              : 0;

        if (dx !== 0 || dy !== 0) {
          graph.translate(dx, dy, true);
        }
      };

      // Move tree to top left corner
      const moveToTopLeft = (nodeId: string) => {
        const node = graph.findById(nodeId);
        if (!node) {
          return;
        }
        graph.focusItem(nodeId, false);
        const centerX = graph.getWidth() / 2;
        const centerY = graph.getHeight() / 2;
        const targetX = 150;
        const targetY = 30;
        const dx = targetX - centerX;
        const dy = targetY - centerY;
        graph.translate(dx, dy);
      };

      // Locate a node by expanding its ancestors and scrolling to it
      const locateNode = (nodeId: string) => {
        if (!treeData) return;
        const expandPath = (root: ProjectTreeNode, targetId: string): boolean => {
          if (root.id === targetId) return true;
          if (root.children) {
            for (const child of root.children) {
              if (expandPath(child, targetId)) {
                root.collapsed = false;
                return true;
              }
            }
          }
          return false;
        };
        if (expandPath(treeData, nodeId)) {
          graph.changeData(treeData);
          graph.layout();
          setTimeout(() => moveToTopLeft(nodeId));
        }
      };
      locateNodeRef.current = locateNode;

      // Handle collapse/expand - listen to all collapse-related shapes
      const handleCollapse = (e: IG6GraphEvent) => {
        const item = e.item;
        if (!item) return;
        const nodeModel = item.getModel() as unknown as ProjectTreeNode;
        const collapsed = !nodeModel.collapsed;
        updateGraphItem(item, { collapsed }, 'collapsed');
        graph.layout();
        // Angular parity: refresh highlight state after layout so newly visible nodes get proper border rendering
        syncGraphNodeVisualState(graph, highlightPathIdsRef.current, currentTreeNodeIdRef.current);
        // If expanding, ensure the first child node is visible
        if (!collapsed && nodeModel.children?.[0]?.id) {
          ensureNodeVisibility(nodeModel.children[0].id);
        }
        e.stopPropagation();
        e.preventDefault();
      };

      ['COLLAPSE_BOX_SHAPE', 'COLLAPSE_ICON_SHAPE', 'COLLAPSE_TEXT_SHAPE'].forEach((shapeName) => {
        graph.on(`${shapeName}:click`, handleCollapse);
      });

      // Handle checkbox click (multi-select)
      graph.on(`${CHECKBOX_SHAPE}:click`, (e: IG6GraphEvent) => {
        checkItem(e.item as unknown as Item);
        e.stopPropagation();
        e.preventDefault();
      });

      // Handle node click - show context menu
      graph.on('node:click', (e: IG6GraphEvent) => {
        const item = e.item;
        if (!item) return;
        // Skip if clicking on collapse shape
        const shape = e.shape;
        const shapeName = shape?.get('name');
        if (shapeName && ['COLLAPSE_BOX_SHAPE', 'COLLAPSE_ICON_SHAPE', 'COLLAPSE_TEXT_SHAPE'].includes(shapeName)) {
          return;
        }
        // Stop event propagation to prevent triggering parent click handlers
        e.stopPropagation();
        e.preventDefault();

        // Update item data before show menu (sync from latest records)
        updateItemDataBeforeShowContextMenu(item as unknown as Item);
        // Get node data
        const nodeModel = item.getModel() as unknown as ProjectTreeNode;

        // Match legacy behavior: left-click opens the context menu, while actual viewing
        // happens through the explicit "View in workbench" action.

        // Get click position from original event
        const originalEvent = e.originalEvent as MouseEvent;
        const x = originalEvent.clientX;
        const y = originalEvent.clientY;

        // Show context menu
        setInterContextMenuVisible(false);
        setContextMenuNode(nodeModel);
        setContextMenuPosition({ x, y });
        setContextMenuVisible(true);
      });

      // Handle inter shape click (interpolation node menu)
      [INTER_BOX_SHAPE, INTER_ICON_SHAPE].forEach((shapeName) => {
        graph.on(`${shapeName}:click`, (e: IG6GraphEvent) => {
          const item = e.item;
          if (!item) return;

          // Update item data before show menu (sync from latest records)
          updateItemDataBeforeShowContextMenu(item as unknown as Item);
          const nodeModel = item.getModel() as unknown as ProjectTreeNode;

          // Get click position
          const originalEvent = e.originalEvent as MouseEvent;
          const x = originalEvent.clientX;
          const y = originalEvent.clientY;

          // Show interpolation context menu
          setContextMenuVisible(false);
          setInterContextMenuNode(nodeModel);
          setInterContextMenuPosition({ x, y });
          setInterContextMenuVisible(true);

          e.stopPropagation();
          e.preventDefault();
        });

        graph.on(`${shapeName}:mouseenter`, (e: IG6GraphEvent) => {
          e.stopPropagation();
          e.preventDefault();
          const item = e.item;
          if (item) {
            updateGraphItem(item, { interHovered: true }, 'inter-hovered');
            // Show tooltip for interpolated items
            const data = item.getModel() as unknown as ProjectTreeNode;
            if (data.isInter) {
              const map = treeItemMapRef.current;
              const nodeData = data as unknown as {
                parentId?: string;
                parentItemName?: string;
                parentCaseId?: string;
                parentCaseName?: string;
              };
              const vmItem = nodeData.parentId ? map.get(nodeData.parentId) : undefined;
              const caseItem = nodeData.parentCaseId ? map.get(nodeData.parentCaseId) : undefined;
              const resolvedData = {
                ...data,
                parentItemName: vmItem?.name || nodeData.parentItemName,
                parentCaseName: caseItem?.name || nodeData.parentCaseName,
              };
              setInterTooltipNode(resolvedData);
              const originalEvent = e.originalEvent as MouseEvent;
              setInterTooltipStyle({
                left: `${originalEvent.clientX + 12}px`,
                top: `${originalEvent.clientY - 10}px`,
              });
              setInterTooltipVisible(true);
            }
          }
        });

        graph.on(`${shapeName}:mouseleave`, (e: IG6GraphEvent) => {
          e.stopPropagation();
          e.preventDefault();
          const item = e.item;
          if (item) {
            updateGraphItem(item, { interHovered: false }, 'inter-hovered');
          }
          setInterTooltipVisible(false);
        });
      });

      // Handle node hover
      graph.on('node:mouseenter', (e: IG6GraphEvent) => {
        e.stopPropagation();
        e.preventDefault();
        const item = e.item;
        if (item) {
          updateGraphItem(item, { hovered: true }, 'hovered');
        }
      });

      graph.on('node:mouseleave', (e: IG6GraphEvent) => {
        e.stopPropagation();
        e.preventDefault();
        const item = e.item;
        if (item) {
          updateGraphItem(item, { hovered: false }, 'hovered');
        }
      });

      // Render data - check if treeData is valid
      if (!treeData) {
        return null;
      }

      // Hide until initial positioning completes to prevent the centered-then-jump flash.
      // Use visibility (instant) instead of opacity (affected by CSS transitions).
      container.style.visibility = 'hidden';

      graph.data(treeData);
      graph.render();
      lastRenderedTreeDataRef.current = treeData;

      // Angular parity: hide the mock root node and all edges between it and the first-level Geometry nodes.
      const hideMockRootNode = () => {
        if (!hasMultiGeometry) return;
        const root = graph.findById(MOCK_ROOT_NODE_ID);
        if (root) {
          graph.hideItem(root);
        }
        graph.getEdges().forEach((edge) => {
          const sourceId = edge.getSource().getID();
          const targetId = edge.getTarget().getID();
          if (sourceId === MOCK_ROOT_NODE_ID || targetId === MOCK_ROOT_NODE_ID) {
            graph.hideItem(edge);
          }
        });
      };
      hideMockRootNode();

      // Position the tree at top-left after layout settles (one animation frame is
      // sufficient because initGraph is already deferred until the Drawer finishes).
      // Lock maxZoom so users can only zoom out from the initial view.
      requestAnimationFrame(() => {
        graph.fitView(20, { ratioRule: 'max' });
        if (graph.getZoom() > 1) {
          graph.zoomTo(1);
        }

        // Position root node at top-left (150, 30) by directly computing its
        // current canvas position instead of relying on focusItem.
        const rootId = treeData.id;
        if (rootId) {
          const rootNode = graph.findById(rootId);
          if (rootNode) {
            const model = rootNode.getModel();
            const canvasPos = graph.getCanvasByPoint(model.x as number, model.y as number);
            graph.translate(150 - canvasPos.x, 30 - canvasPos.y);
          }
        }

        hideMockRootNode();
        graph.set('maxZoom', Math.min(graph.getZoom(), 1));
        container.style.visibility = 'visible';
      });

      syncGraphNodeVisualState(graph, highlightPathIdsRef.current, currentTreeNodeIdRef.current);

      // Handle resize with debounce for better performance
      let resizeTimer: ReturnType<typeof setTimeout> | null = null;
      const handleResize = () => {
        if (resizeTimer) {
          clearTimeout(resizeTimer);
        }
        resizeTimer = setTimeout(() => {
          if (graphRef.current && container) {
            graphRef.current.changeSize(container.clientWidth, container.clientHeight);
          }
        }, 150);
      };

      const resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(container);

      // Store cleanup function in a way that can be returned
      (resizeObserver as unknown as { cleanup?: () => void }).cleanup = () => {
        if (resizeTimer) {
          clearTimeout(resizeTimer);
        }
        resizeObserver.disconnect();
      };

      return resizeObserver;
    };

    let resizeObserver: ResizeObserver | null = null;

    // Always defer graph init so the Drawer open-animation has time to finish
    // and the container reaches its final dimensions.  Without this, re-opens
    // (where clientWidth is non-zero from the previous session) would init the
    // graph while the Drawer is still mid-slide, producing wrong fitView results.
    const DRAWER_SETTLE_MS = 300;
    const timer = setTimeout(() => {
      if (containerRef.current && treeData) {
        resizeObserver = initGraph();
      }
    }, DRAWER_SETTLE_MS);
    return () => {
      clearTimeout(timer);
      resizeObserver?.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, treeData?.id, isLoading, hasMultiGeometry, syncGraphNodeVisualState]); // treeData.id changes when mock root is injected

  // Destroy graph on component unmount
  useEffect(() => {
    return () => {
      if (graphRef.current) {
        graphRef.current.destroy();
        graphRef.current = null;
      }
    };
  }, []);

  // Track the treeData reference that was used for the last graph.data() / graph.changeData()
  // so we can skip redundant updates during the initial render.
  const lastRenderedTreeDataRef = useRef<ProjectTreeNode | null>(null);

  useEffect(() => {
    if (visible) return;
    clearCheckedNodeList();
    clearWsWatches();
    closeDrawerTransientUi();
    lastRenderedTreeDataRef.current = null;
    if (graphRef.current) {
      graphRef.current.destroy();
      graphRef.current = null;
    }
  }, [clearCheckedNodeList, clearWsWatches, closeDrawerTransientUi, visible]);

  // Update graph data when treeData changes after initial render (e.g., after refetch).
  // The graph init effect above depends on treeData?.id which won't change when the root node
  // stays the same, so we need a separate effect to push updated node data (status changes,
  // new nodes) into the existing graph instance.
  useEffect(() => {
    const graph = graphRef.current;
    if (!graph || !treeData || !visible) return;

    // Skip if this is the same treeData used during graph init
    if (lastRenderedTreeDataRef.current === treeData) return;
    lastRenderedTreeDataRef.current = treeData;

    // Hide container during changeData + layout to prevent the brief flash
    // where nodes appear at intermediate positions before layout completes.
    const container = graph.get('container') as HTMLElement | null;
    if (container) {
      container.style.visibility = 'hidden';
    }

    // Preserve the current viewport (zoom + pan) across the data update.
    // changeData triggers a full re-layout which can reset the viewport transform.
    const prevZoom = graph.getZoom();
    const prevMatrix = graph.getGroup().getMatrix()?.slice() ?? null;

    graph.changeData(treeData);

    // Restore viewport so running-node progress refreshes don't re-center the tree.
    if (prevMatrix) {
      graph.getGroup().setMatrix(prevMatrix);
    }
    if (Math.abs(graph.getZoom() - prevZoom) > 0.001) {
      graph.zoomTo(prevZoom);
    }

    syncGraphNodeVisualState(graph, highlightPathIdsRef.current, currentTreeNodeIdRef.current);

    // Re-hide mock root node if multi-geometry
    if (hasMultiGeometry) {
      const mockRoot = graph.findById(MOCK_ROOT_NODE_ID);
      if (mockRoot) {
        graph.hideItem(mockRoot);
      }
      graph.getEdges().forEach((edge) => {
        const sourceId = edge.getSource().getID();
        const targetId = edge.getTarget().getID();
        if (sourceId === MOCK_ROOT_NODE_ID || targetId === MOCK_ROOT_NODE_ID) {
          graph.hideItem(edge);
        }
      });
    }

    // Reveal container after layout settles (one frame for G6 layout to finish).
    requestAnimationFrame(() => {
      if (container) {
        container.style.visibility = 'visible';
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeData, visible, syncGraphNodeVisualState]);

  const handleClose = () => {
    onVisibleChange(false);
  };

  const handleDrawerClick = (e: React.MouseEvent) => {
    // Prevent event bubbling to parent elements
    e.stopPropagation();
  };

  const handleMaskClick = (e: React.MouseEvent<Element> | React.KeyboardEvent<Element> | KeyboardEvent) => {
    // Prevent event bubbling when clicking on mask
    if ('stopPropagation' in e) {
      e.stopPropagation();
    }
    handleClose();
  };

  // Build context menu groups
  const contextMenuGroups = useMemo((): FlowDropdownGroup[] => {
    if (!contextMenuNode) return [];

    const groups: FlowDropdownGroup[] = [];
    const preferNewTab = usePreferenceStore.getState().openInNewTab();
    const openInNewTabMode = isPrimaryModifierPressed ? !preferNewTab : preferNewTab;

    // Slot: Node name
    groups.push({
      type: 'slot',
      content: (
        <div className={styles.contextMenuMetaItem}>
          <div className={styles.contextMenuNameText}>{contextMenuNode.name}</div>
        </div>
      ),
    });

    // Slot: Node ID with copy button
    groups.push({
      type: 'slot',
      content: (
        <div className={styles.contextMenuMetaItem}>
          <div className={styles.contextMenuIdRow}>
            <span className={styles.contextMenuIdText}>{contextMenuNode.id}</span>
            <Tooltip title="Copy">
              <button
                type="button"
                aria-label="Copy ID"
                className={styles.contextMenuCopyButton}
                onMouseDown={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    await copyText(contextMenuNode.id);
                    messageApi.success('Copied');
                  } catch (error) {
                    messageApi.error('Copy error');
                    console.error('Copy error:', error);
                  }
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onKeyDown={async (e) => {
                  if (e.key !== 'Enter' && e.key !== ' ') return;
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    await copyText(contextMenuNode.id);
                    messageApi.success('Copied');
                  } catch (error) {
                    messageApi.error('Copy error');
                    console.error('Copy error:', error);
                  }
                }}
              >
                <CopyOutline style={{ fontSize: '14px' }} />
              </button>
            </Tooltip>
          </div>
        </div>
      ),
    });

    // Action items
    const actionItems: FlowDropdownItem[] = [];

    if (!viewMode) {
      const nodeData = contextMenuNode as unknown as {
        parentCaseId?: string;
        parentCaseDeleted?: boolean;
        parentItemDeleted?: boolean;
      };
      const isDisabled = !!(nodeData.parentCaseId && (nodeData.parentCaseDeleted || nodeData.parentItemDeleted));
      actionItems.push({
        key: 'copy-to-new-run',
        label: (
          <span>
            <FileDescriptionOutline style={{ fontSize: '14px', marginRight: '8px' }} />
            {openInNewTabMode ? 'New run in new tab' : 'New run'}
          </span>
        ),
        disabled: isDisabled || false,
      });
    }

    actionItems.push({
      key: 'view',
      label: (
        <span>
          <ShareBoxOutline style={{ fontSize: '14px', marginRight: '8px' }} />
          {openInNewTabMode ? 'Open in new tab' : 'View in workbench'}
        </span>
      ),
    });

    if (!viewMode) {
      if (contextMenuNode.itemType === 'VolumeMesh' && contextMenuNode.displayStatus === 'completed') {
        actionItems.push({
          key: 'new-project',
          label: (
            <span>
              <ProjectOutline style={{ fontSize: '14px', marginRight: '8px' }} />
              New project
            </span>
          ),
        });
      }

      const forkCaseVisible =
        contextMenuNode.itemType === 'Case' &&
        contextMenuNode.displayStatus !== 'error' &&
        contextMenuNode.displayStatus !== 'diverged';
      if (forkCaseVisible) {
        const nodeData = contextMenuNode as unknown as {
          parentCaseId?: string;
          parentItemDeleted?: boolean;
        };
        actionItems.push({
          key: 'fork',
          label: (
            <span>
              <GitForkOutline style={{ fontSize: '14px', marginRight: '8px' }} />
              {openInNewTabMode ? 'Fork in new tab' : 'Fork'}
            </span>
          ),
          disabled: !!(nodeData.parentCaseId && nodeData.parentItemDeleted),
        });
      }

      if (contextMenuNode.itemType === 'VolumeMesh') {
        actionItems.push({
          key: 'volume-interpolation',
          label: (
            <span>
              <InsertOutline style={{ fontSize: '14px', marginRight: '8px' }} />
              Interpolation
            </span>
          ),
        });
      } else if (forkCaseVisible) {
        actionItems.push({
          key: 'case-interpolation',
          label: (
            <span>
              <InsertOutline style={{ fontSize: '14px', marginRight: '8px' }} />
              Interpolation
            </span>
          ),
        });
      }

      actionItems.push({
        key: 'edit',
        'data-testid': 'project-tree-context-menu-edit-item',
        label: (
          <span>
            <PencilOutline style={{ fontSize: '14px', marginRight: '8px' }} />
            Edit
          </span>
        ),
      });

      actionItems.push({
        key: 'details',
        label: (
          <span>
            <FileDescriptionOutline style={{ fontSize: '14px', marginRight: '8px' }} />
            Details
          </span>
        ),
      });
    }

    if (actionItems.length > 0) {
      groups.push({ items: actionItems });
    }

    // Expand/Collapse all (if has children)
    if (contextMenuNode.children && contextMenuNode.children.length > 0) {
      groups.push({
        items: [
          {
            key: 'expand-all',
            label: (
              <span>
                <ArrowDownDoubleOutline style={{ fontSize: '14px', marginRight: '8px' }} />
                Expand all sub-items
              </span>
            ),
          },
          {
            key: 'collapse-all',
            label: (
              <span>
                <ArrowUpDoubleOutline style={{ fontSize: '14px', marginRight: '8px' }} />
                Collapse all sub-items
              </span>
            ),
          },
        ],
      });
    }

    // Delete (if not root and not Geometry)
    const parentId = (contextMenuNode as unknown as { parentId?: string }).parentId;
    if (parentId && contextMenuNode.itemType !== 'Geometry') {
      groups.push({
        items: [{ key: 'delete', label: 'Delete', icon: <TrashOutline style={{ fontSize: '16px' }} />, danger: true }],
      });
    }

    return groups;
  }, [contextMenuNode, viewMode, isPrimaryModifierPressed, messageApi]);

  const handleContextMenuClick = useCallback(
    async (key: string) => {
      if (!contextMenuNode) return;

      setContextMenuVisible(false);

      // Map menu key to the spec-defined action label for `PT-Dropdown-Action`.
      // Labels follow the PM event catalog example-values column.
      const actionLabel: Record<string, string> = {
        'copy-to-new-run': 'New run',
        view: 'View in workbench',
        'new-project': 'New project',
        fork: 'Fork',
        'volume-interpolation': 'Volume Interpolation',
        'case-interpolation': 'Case Interpolation',
        edit: 'Edit',
        details: 'Details',
        'expand-all': 'Expand all sub-items',
        'collapse-all': 'Collapse all sub-items',
        delete: 'Delete',
      };
      if (actionLabel[key]) {
        logBehavior('PT-Dropdown-Action', { action: actionLabel[key] });
      }

      const prefNew = usePreferenceStore.getState().openInNewTab();
      const modifierPressed = isPrimaryModifierPressed;
      const openInNewTab = modifierPressed ? !prefNew : prefNew;

      switch (key) {
        case 'copy-to-new-run': {
          try {
            const name = contextMenuNode.name.replace(/^(Case_|SurfaceMesh_|VolumeMesh_)/, '');
            const newDraft = await projectApi.createDraft({
              name: `${name} (copy)`,
              sourceItemId: contextMenuNode.id,
              sourceItemType: contextMenuNode.itemType || contextMenuNode.type,
              projectId: currentProject.id,
              solverVersion: contextMenuNode.solverVersion,
            });
            messageApi.success('New run created successfully');
            if (onNewDraftEvent) {
              onNewDraftEvent(contextMenuNode);
            }
            handleDraftCreated(newDraft, { action: 'copy', node: contextMenuNode, modifierPressed });
            queryClient.invalidateQueries({ queryKey: ['project-tree-items', currentProject.id] });
          } catch (error) {
            messageApi.error('Failed to create new run');
            console.error('Create new run error:', error);
          }
          break;
        }
        case 'view': {
          const nodeProjectId = contextMenuNode.projectId;
          const nodeParentItemProjectId = (contextMenuNode as unknown as { parentItemProjectId?: string })
            .parentItemProjectId;

          const targetProjectId =
            nodeProjectId && nodeProjectId !== currentProject.id
              ? nodeProjectId
              : nodeParentItemProjectId && nodeParentItemProjectId !== currentProject.id
                ? nodeParentItemProjectId
                : undefined;

          if (targetProjectId) {
            let navId = contextMenuNode.id;
            let navType = String(contextMenuNode.itemType || contextMenuNode.type);
            if (
              nodeProjectId === currentProject.id &&
              targetProjectId === nodeParentItemProjectId &&
              contextMenuNode.parentId
            ) {
              navId = contextMenuNode.parentId;
              navType = 'VolumeMesh';
            }
            openWorkbenchItemInNewTab(targetProjectId, navId, navType);
          } else if (openInNewTab) {
            openWorkbenchItemInNewTab(
              currentProject.id,
              contextMenuNode.id,
              String(contextMenuNode.itemType || contextMenuNode.type),
            );
            onVisibleChange(false);
          } else if (onViewEvent) {
            onViewEvent({
              item: {
                ...contextMenuNode,
                type: contextMenuNode.itemType || contextMenuNode.type,
                projectId: nodeProjectId,
                parentId: contextMenuNode.parentId,
              } as ProjectItem,
              event: undefined,
            });
          } else {
            openWorkbenchItemInNewTab(
              currentProject.id,
              contextMenuNode.id,
              String(contextMenuNode.itemType || contextMenuNode.type),
            );
          }
          break;
        }
        case 'new-project': {
          setNewProjectCloneNode(contextMenuNode);
          setNewProjectModalVisible(true);
          break;
        }
        case 'fork': {
          try {
            const name = contextMenuNode.name.replace(/^(Case_|SurfaceMesh_|VolumeMesh_)/, '');
            const newDraft = await projectApi.createDraft({
              name: `${name} (fork)`,
              sourceItemId: contextMenuNode.id,
              sourceItemType: contextMenuNode.itemType || contextMenuNode.type,
              projectId: currentProject.id,
              solverVersion: contextMenuNode.solverVersion,
              forkCase: true,
            });
            messageApi.success('Fork created successfully');
            handleDraftCreated(newDraft, { action: 'fork', node: contextMenuNode, modifierPressed });
            queryClient.invalidateQueries({ queryKey: ['project-tree-items', currentProject.id] });
          } catch (error) {
            messageApi.error('Failed to fork item');
            console.error('Fork error:', error);
          }
          break;
        }
        case 'volume-interpolation':
        case 'case-interpolation': {
          if (currentProject.rootItemType === 'Geometry' || currentProject.rootItemType === 'SurfaceMesh') {
            modalApi.confirm({
              title: 'Interpolation from this workflow is not fully supported',
              width: 520,
              content: (
                <div>
                  You are starting an interpolation from a Geometry or Surface-mesh workflow. In the current release,
                  this path may produce unexpected behavior — the entities list, boundary conditions, and mesh
                  refinements may not reflect the target volume mesh correctly.
                  <br />
                  <br />
                  <strong>Recommended:</strong> start the interpolation from a Volume workflow (or use{' '}
                  <strong>&quot;New project&quot;</strong> on a volume-mesh node to create a volume-only project first).
                  <br />
                  <br />
                  You may continue at your own risk. If you hit an issue, please contact support and include the project
                  ID and case ID so we can investigate.
                </div>
              ),
              okText: 'Continue',
              okButtonProps: {
                type: 'primary',
                style: {
                  backgroundColor: 'var(--sys-color-primary-default)',
                  borderColor: 'var(--sys-color-primary-default)',
                },
              },
              cancelText: 'Cancel',
              onOk: () => {
                openInterItemSelectModal(contextMenuNode);
              },
            });
          } else {
            openInterItemSelectModal(contextMenuNode);
          }
          break;
        }
        case 'edit': {
          setEditItemTargetNode(contextMenuNode);
          setEditItemVisible(true);
          break;
        }
        case 'details': {
          setDetailsDrawerNode(contextMenuNode);
          setDetailsDrawerVisible(true);
          break;
        }
        case 'expand-all': {
          if (!graphRef.current) break;
          recursiveSetCollapsed(contextMenuNode, false);
          const item = graphRef.current.findById(contextMenuNode.id);
          if (item) {
            updateGraphItem(item, { collapsed: false, children: contextMenuNode.children }, 'default');
            graphRef.current.layout();
          }
          break;
        }
        case 'collapse-all': {
          if (!graphRef.current) break;
          recursiveSetCollapsed(contextMenuNode, true);
          const item = graphRef.current.findById(contextMenuNode.id);
          if (item) {
            updateGraphItem(item, { collapsed: true, children: contextMenuNode.children }, 'default');
            graphRef.current.layout();
          }
          break;
        }
        case 'delete': {
          beforeDeleteNode(contextMenuNode);
          break;
        }
      }
    },
    [
      contextMenuNode,
      currentProject.id,
      isPrimaryModifierPressed,
      recursiveSetCollapsed,
      beforeDeleteNode,
      onNewDraftEvent,
      onVisibleChange,
      queryClient,
      onViewEvent,
      openWorkbenchItemInNewTab,
      openInterItemSelectModal,
      handleDraftCreated,
      updateGraphItem,
      messageApi,
      modalApi,
      currentProject.rootItemType,
    ],
  );

  useLayoutEffect(() => {
    if (!contextMenuVisible) {
      return;
    }

    const nextPosition = clampMenuPositionToViewport(contextMenuPosition, contextMenuContainerRef.current);
    if (nextPosition.x !== contextMenuPosition.x || nextPosition.y !== contextMenuPosition.y) {
      setContextMenuPosition(nextPosition);
    }
  }, [contextMenuGroups, contextMenuPosition, contextMenuVisible]);

  useLayoutEffect(() => {
    if (!interContextMenuVisible) {
      return;
    }

    const nextPosition = clampMenuPositionToViewport(interContextMenuPosition, interContextMenuContainerRef.current);
    if (nextPosition.x !== interContextMenuPosition.x || nextPosition.y !== interContextMenuPosition.y) {
      setInterContextMenuPosition(nextPosition);
    }
  }, [interContextMenuNode, interContextMenuPosition, interContextMenuVisible, isPrimaryModifierPressed]);

  return (
    <>
      <Drawer
        open={visible}
        onClose={handleMaskClick}
        placement="right"
        size="calc(100vw - 300px)"
        closable={false}
        zIndex={999}
        maskClosable={true}
        onClick={handleDrawerClick}
        styles={{
          body: { padding: 0, overflow: 'hidden' },
          header: {
            padding: 'var(--ref-space-m, 12px) var(--ref-space-l, 16px)',
            borderBottom: '1px solid var(--sys-border-color-default, #e0e2e7)',
          },
        }}
        data-testid="project-tree"
        title={
          <div className={styles.drawer_title}>
            <div className={styles.title_row}>
              <Tree2Outline className={styles.tree_icon} style={{ fontSize: '16px' }} />
              <Breadcrumb
                items={[
                  ...folderBreadcrumb.map((f) => ({
                    title: (
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          openWorkspaceFolderInNewTab(f.id);
                        }}
                      >
                        {f.name}
                      </a>
                    ),
                  })),
                  { title: <span>{currentProject.name}</span> },
                ]}
              />
              {(() => {
                const visibleStatItems = [
                  {
                    key: 'completed',
                    testId: 'project-tree-stat-completed',
                    label: 'Completed',
                    count: itemStats.completed,
                    icon: <CircleCheckFill style={{ fontSize: '12px', color: 'var(--sys-success-color-default)' }} />,
                  },
                  {
                    key: 'running',
                    testId: 'project-tree-stat-running',
                    label: 'Running',
                    count: itemStats.running,
                    icon: <Charts2Outline style={{ fontSize: '12px', color: 'var(--ref-color-blue-05)' }} />,
                  },
                  {
                    key: 'post-process',
                    testId: 'project-tree-stat-post-process',
                    label: 'Post process',
                    count: itemStats.postProcess,
                    icon: <ChartsSetOutline style={{ fontSize: '12px', color: 'var(--ref-color-blue-05)' }} />,
                  },
                  {
                    key: 'post-process-failed',
                    testId: 'project-tree-stat-post-process-failed',
                    label: 'Post-processing failed',
                    count: itemStats.postProcessFailed,
                    icon: (
                      <CircleHalfCheckOutline style={{ fontSize: '12px', color: 'var(--sys-warning-color-default)' }} />
                    ),
                  },
                  {
                    key: 'error',
                    testId: 'project-tree-stat-error',
                    label: 'Error',
                    count: itemStats.error,
                    icon: <CircleXFill style={{ fontSize: '12px', color: 'var(--sys-error-color-default)' }} />,
                  },
                  {
                    key: 'diverged',
                    testId: 'project-tree-stat-diverged',
                    label: 'Diverged',
                    count: itemStats.diverged,
                    icon: (
                      <CircleTrendingDown2Fill
                        style={{ fontSize: '12px', color: 'var(--sys-warning-color-default)' }}
                      />
                    ),
                  },
                ].filter((it) => it.count > 0);

                if (visibleStatItems.length === 0) {
                  return null;
                }

                return (
                  <div className={styles.stat_panel} data-testid="project-tree-stat-panel">
                    {visibleStatItems.map((it, idx) => (
                      <React.Fragment key={it.key}>
                        {idx > 0 ? <span className={styles.stat_divider} /> : null}
                        <div className={styles.stat_item} data-testid={it.testId}>
                          {it.icon}
                          <span className={styles.label}>
                            {it.label}: {it.count}
                          </span>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                );
              })()}
            </div>
            <div>
              {isDraftListLoading ? (
                <div style={{ padding: '8px 0', color: 'var(--sys-font-color-auxiliary)' }}>Loading drafts...</div>
              ) : draftList.length > 0 ? (
                <DraftList
                  list={draftList}
                  visible={visible}
                  activeId={activeDraftId}
                  currentWorkbenchDraftId={currentWorkbenchDraftId}
                  selectedIds={selectedDraftIds}
                  viewMode={viewMode}
                  onActiveChange={handleActiveDraftChange}
                  onSelectedIdsChange={setSelectedDraftIds}
                  onViewInWorkbench={handleViewDraftInWorkbench}
                  onRename={handleRenameDraft}
                  onDelete={handleDeleteDraft}
                  onBatchDelete={handleBatchDeleteDrafts}
                  onClearSelection={() => setSelectedDraftIds([])}
                  onHoverChange={(draftId, isHover) => {
                    setHoverDraftId(isHover ? draftId : '');
                  }}
                  isRenameDisabled={isDraftRenameDisabled}
                  isDeleteDisabled={isDraftDeleteDisabled}
                />
              ) : (
                <div style={{ padding: '8px 0', color: 'var(--sys-font-color-auxiliary)' }}>No drafts</div>
              )}
            </div>
          </div>
        }
      >
        <div className={styles.graph_container} onClick={handleDrawerClick} onMouseDown={handleDrawerClick}>
          {isLoading ? (
            <div className={styles.graph_loading} data-testid="project-tree-graph-loading">
              <Spin />
            </div>
          ) : (
            <div ref={containerRef} className={styles.graph_wrapper} />
          )}

          <div className={styles.shortcut_button_area}>
            <ProjectTreeShortcutButton />
          </div>

          {checkedNodeIds.length > 0 && (
            <div className={styles.tree_batch_actions}>
              <div className={styles.batch_actions_panel}>
                <Space.Compact>
                  <Button size="small" type="dashed">
                    {checkedNodeIds.length} selected
                  </Button>
                  <Button
                    size="small"
                    type="dashed"
                    onClick={(e) => {
                      e.stopPropagation();
                      clearCheckedNodeList();
                    }}
                    icon={<CloseOutline style={{ fontSize: '16px' }} />}
                  />
                </Space.Compact>
                <span className={styles.seperator} />
                <div className={styles.batch_actions_buttons}>
                  <Tooltip title={compareBtnDisabled ? 'Select at least 2 cases to compare.' : undefined}>
                    <Button
                      size="small"
                      disabled={compareBtnDisabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        compareCases();
                      }}
                      icon={<CompareOutline style={{ fontSize: '16px' }} />}
                    >
                      Compare
                    </Button>
                  </Tooltip>
                  {!viewMode && (
                    <Button
                      danger
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        batchDelete();
                      }}
                      icon={<TrashOutline style={{ fontSize: '16px' }} />}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Details Drawer */}
        <ProjectTreeDetailsDrawer
          open={detailsDrawerVisible}
          node={
            detailsDrawerNode
              ? {
                  id: detailsDrawerNode.id,
                  itemType: (detailsDrawerNode.itemType ||
                    detailsDrawerNode.type) as unknown as WorkspaceItemTypeFromWorkspace,
                }
              : null
          }
          currentProject={currentProject}
          viewMode={!!viewMode}
          onClose={() => {
            setDetailsDrawerVisible(false);
            setDetailsDrawerNode(null);
          }}
          onCloseAfterUpdate={async (payload) => {
            // Align with legacy behavior: refresh current node data after Details update.
            try {
              const latest = await projectApi.getItem(payload.id, payload.type as unknown as WorkspaceItemType);
              const graph = graphRef.current;
              if (graph) {
                const item = graph.findById(payload.id);
                if (item) {
                  const nodeModel = item.getModel() as unknown as ProjectTreeNode;
                  // IMPORTANT:
                  // - G6 node `type` must remain as the registered node type (e.g. 'flow-rect').
                  // - Backend `type` is the business type (Geometry/SurfaceMesh/VolumeMesh/Case) and should map to `itemType`.
                  const g6NodeType = nodeModel.type;
                  const businessType = (nodeModel.itemType ??
                    (latest as ProjectItem).type) as ProjectTreeNode['itemType'];

                  updateGraphItem(
                    item,
                    {
                      ...nodeModel, // preserve graph-only fields: children/collapsed/highlight/checked/hovered/...
                      ...latest, // update business fields: name/tags/displayStatus/solverVersion/...
                      type: g6NodeType, // keep G6 node type
                      itemType: businessType, // keep business type
                    },
                    'default',
                  );
                }
              }
            } catch (error) {
              console.error('[ProjectTree][Details] refresh node failed', error);
            }
            // Fallback: refresh whole tree data
            queryClient.invalidateQueries({ queryKey: ['project-tree-items', currentProject.id] });
          }}
        />

        {/* New Project Modal */}
        {newProjectCloneNode && (
          <CreateProjectModal
            visible={newProjectModalVisible}
            onCancel={() => {
              setNewProjectModalVisible(false);
              setNewProjectCloneNode(null);
            }}
            onSuccess={() => {
              messageApi.success('New project created successfully');
              openWorkspaceFolderInNewTab(
                (newProjectCloneNode as unknown as { parentFolderId?: string }).parentFolderId,
                '0',
              );
              setNewProjectModalVisible(false);
              setNewProjectCloneNode(null);
            }}
            isClone={true}
            cloneType="Mesh"
            cloneNode={{
              id: newProjectCloneNode.id,
              name: newProjectCloneNode.name,
              parentFolderId: (newProjectCloneNode as unknown as { parentFolderId?: string }).parentFolderId || '',
            }}
          />
        )}

        {/* Interpolation Select Modal */}
        <InterItemSelectModal
          visible={interItemSelectModalVisible}
          submitLoading={interItemSelectModalLoading}
          operationNode={interOperationNode}
          rootFolderId={interpolationRootFolderId}
          currentProjectId={currentProject.id}
          currentProjectRootItemType={currentProject.rootItemType}
          data={interItemSelectData}
          onCancel={closeInterItemSelectModal}
          onSubmit={handleInterItemSelectSubmit}
        />

        <BatchDeleteAssetModal
          open={batchDeleteModalOpen}
          assets={batchDeleteModalAssets}
          onClose={(isSuccess) => {
            setBatchDeleteModalOpen(false);
            if (!isSuccess) return;

            const graph = graphRef.current;
            if (!graph) return;

            const nodeList = batchDeleteModalNodes;
            const ids = collectItemIdsFromFlatTree(nodeList.map((n) => n.id));

            ids.forEach((id) => {
              graph.removeItem(id);
            });

            nodeList.forEach((node) => {
              if (!node.parentNodeId) return;
              const parentNode = graph.findById(node.parentNodeId);
              if (!parentNode) return;
              const dataModel = parentNode.getModel() as unknown as ProjectTreeNode;
              const newChildren = dataModel.children?.filter((item) => item.id !== node.id);
              dataModel.children = newChildren;
              updateGraphItem(parentNode, { children: newChildren }, 'default');
              const children = parentNode.get('children');
              if (Array.isArray(children)) {
                parentNode.set(
                  'children',
                  children.filter((item: unknown) => !(item as { destroyed?: boolean }).destroyed),
                );
              }
            });

            graph.refresh();
            queryClient.invalidateQueries({ queryKey: ['project-tree-items', currentProject.id] });
            queryClient.invalidateQueries({ queryKey: ['project-tree-draft-list', currentProject.id] });

            const currentViewingId = currentItemAssetIdRef.current;
            if (currentViewingId && ids.has(currentViewingId)) {
              switchToClosestParentOrRoot(ids);
            }

            setCheckedNodeIds((prev) => prev.filter((id) => !ids.has(id)));
            messageApi.success('Asset deleted');
          }}
        />

        {/* Rename Draft Modal */}
        <Modal
          title="Rename draft"
          open={renameDraftVisible}
          confirmLoading={renameDraftLoading}
          okText="Save"
          cancelText="Cancel"
          onOk={async () => {
            if (!renameDraftTargetId) return;
            const nextName = renameDraftName.trim();
            if (!nextName) {
              messageApi.warning('Name is required');
              return;
            }
            try {
              setRenameDraftLoading(true);
              await projectApi.updateItem({ id: renameDraftTargetId, type: 'Draft', name: nextName });
              queryClient.invalidateQueries({ queryKey: ['project-tree-draft-list', currentProject.id] });
              // Sync renamed draft name to workbench breadcrumb & project settings
              if (effectiveCurrentItem?.type === 'Draft' && effectiveCurrentItem.id === renameDraftTargetId) {
                updateCurrentItem({ ...effectiveCurrentItem, name: nextName });
              }
              setRenameDraftVisible(false);
            } finally {
              setRenameDraftLoading(false);
            }
          }}
          onCancel={() => {
            setRenameDraftVisible(false);
          }}
        >
          <Input value={renameDraftName} onChange={(e) => setRenameDraftName(e.target.value)} maxLength={80} />
        </Modal>

        {/* Edit Item Modal (Case / SurfaceMesh / VolumeMesh / Geometry) */}
        <EditItemModal
          visible={editItemVisible}
          item={
            editItemTargetNode
              ? ({
                  id: editItemTargetNode.id,
                  name: editItemTargetNode.name,
                  type: (editItemTargetNode.itemType || editItemTargetNode.type) as WorkspaceItemType,
                  tags: (editItemTargetNode as unknown as { tags?: string[] }).tags ?? [],
                  parentFolderId: currentProject.parentFolderId || '',
                } as WorkspaceItem)
              : null
          }
          onCancel={() => {
            setEditItemVisible(false);
            setEditItemTargetNode(null);
          }}
          onSuccess={() => {
            // Refresh tree data to reflect name/tags changes
            queryClient.invalidateQueries({ queryKey: ['project-tree-items', currentProject.id] });
            setEditItemVisible(false);
            setEditItemTargetNode(null);
          }}
        />
      </Drawer>

      {/* Context Menu - portaled to body to bypass Drawer overflow/transform */}
      {contextMenuVisible &&
        contextMenuNode &&
        createPortal(
          <>
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1049,
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setContextMenuVisible(false);
              }}
            />
            <div
              ref={contextMenuContainerRef}
              style={{
                position: 'fixed',
                left: contextMenuPosition.x,
                top: contextMenuPosition.y,
                zIndex: 1050,
              }}
            >
              <FlowMenu
                groups={contextMenuGroups}
                onClick={handleContextMenuClick}
                stopPropagation
                className={styles.contextMenu}
                data-testid="project-tree-context-menu"
              />
            </div>
          </>,
          document.body,
        )}

      {/* Interpolation hover tooltip */}
      {interTooltipVisible &&
        interTooltipNode &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              zIndex: 1000,
              background: 'var(--sys-background-color-default, #fff)',
              border: '1px solid var(--sys-divider-color-default, #e0e2e7)',
              borderRadius: 4,
              padding: '8px 12px',
              boxShadow: '0 3px 6px -4px rgba(0,0,0,0.12), 0 6px 16px 0 rgba(0,0,0,0.08)',
              pointerEvents: 'none',
              ...interTooltipStyle,
            }}
          >
            <div style={{ fontSize: 11, color: 'var(--sys-font-color-auxiliary, #8c8f96)', marginBottom: 4 }}>
              Interpolated from
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                lineHeight: '22px',
                fontSize: 12,
                color: 'var(--sys-font-color-primary, #1d2129)',
              }}
            >
              <CaseOutline style={{ fontSize: 14, color: 'var(--sys-font-color-auxiliary, #8c8f96)', flex: 'none' }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                {(interTooltipNode as unknown as { parentCaseName?: string }).parentCaseName || ''}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                lineHeight: '22px',
                fontSize: 12,
                color: 'var(--sys-font-color-primary, #1d2129)',
              }}
            >
              <VolumeOutline
                style={{ fontSize: 14, color: 'var(--sys-font-color-auxiliary, #8c8f96)', flex: 'none' }}
              />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                {(interTooltipNode as unknown as { parentItemName?: string }).parentItemName || ''}
              </span>
            </div>
          </div>,
          document.body,
        )}

      {/* Interpolation Context Menu - portaled to body to bypass Drawer overflow/transform */}
      {interContextMenuVisible &&
        interContextMenuNode &&
        createPortal(
          <>
            <div
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1049,
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setInterContextMenuVisible(false);
              }}
            />
            <div
              ref={interContextMenuContainerRef}
              style={{
                position: 'fixed',
                left: interContextMenuPosition.x,
                top: interContextMenuPosition.y,
                zIndex: 1050,
              }}
            >
              <FlowMenu
                groups={(() => {
                  const interGroups: FlowDropdownGroup[] = [];
                  const node = interContextMenuNode;

                  interGroups.push({
                    type: 'slot',
                    content: (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 12px',
                          opacity: node.interItemDeleted ? 0.5 : 1,
                        }}
                      >
                        <div style={{ fontSize: 20, color: 'var(--sys-font-color-auxiliary, #8c8f96)', flex: 'none' }}>
                          {node.interItemType === 'VolumeMesh' ? <VolumeOutline /> : <CaseOutline />}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '12px', color: 'var(--fc-text-secondary)' }}>
                            {node.isInterRefOuterProject ? node.interItemProjectName : 'Current project'}
                          </div>
                          <div
                            style={{
                              fontSize: '14px',
                              fontWeight: 500,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {node.interItemName || 'Unknown'}
                          </div>
                        </div>
                      </div>
                    ),
                  });

                  if (node.interItemDeleted) {
                    interGroups.push({
                      type: 'slot',
                      content: (
                        <div
                          style={{
                            fontSize: '12px',
                            color: 'var(--fc-error)',
                            padding: '8px 12px',
                          }}
                        >
                          The {node.interItemType === 'VolumeMesh' ? 'volume mesh' : 'case'} has been deleted
                        </div>
                      ),
                    });
                  } else {
                    const prefNew = usePreferenceStore.getState().openInNewTab();
                    const showNewTab = isPrimaryModifierPressed ? !prefNew : prefNew;
                    interGroups.push({
                      items: [
                        {
                          key: 'view-inter-item',
                          label: <span>{showNewTab ? 'Open in new tab' : 'View in workbench'}</span>,
                        },
                      ],
                    });
                  }

                  return interGroups;
                })()}
                onClick={(key) => {
                  if (key === 'view-inter-item' && interContextMenuNode) {
                    const node = interContextMenuNode;
                    const projectId = node.interItemProjectId || currentProject.id;
                    if (node.interItemId && node.interItemType) {
                      navigateToProject(
                        `/workbench/${projectId}?id=${node.interItemId}&type=${node.interItemType}`,
                        getAuthPath,
                      );
                    }
                  }
                  setInterContextMenuVisible(false);
                }}
                stopPropagation
                className={styles.contextMenu}
                data-testid="project-tree-inter-context-menu"
              />
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default ProjectTree;
